(function () {
  'use strict';

  const theme = window.PellairTheme && typeof window.PellairTheme === 'object'
    ? window.PellairTheme
    : {};

  if (theme.__runtimeVersion && typeof theme.destroyAll === 'function') {
    try { theme.destroyAll(); } catch (error) {}
  }

  const html = document.documentElement;
  const routes = theme.routes || {};
  const strings = theme.strings || {};
  const osReduceMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };
  const reduceMotion = {
    get matches() {
      return osReduceMotion.matches ||
        theme.motionLevel === 'reduced' ||
        theme.motionLevel === 'none';
    }
  };
  const controllerRecords = new Set();
  const controllerMaps = new WeakMap();
  const lifecycleScope = createScope();
  let uid = 0;

  html.classList.remove('no-js');
  html.classList.add('js');
  html.classList.remove('cart-drawer-is-open', 'mobile-nav-open');
  document.body.classList.remove('cart-drawer-is-open', 'mobile-nav-open');

  function createScope() {
    const cleanups = [];
    let destroyed = false;

    function add(cleanup) {
      if (typeof cleanup !== 'function') return cleanup;
      if (destroyed) {
        try { cleanup(); } catch (error) {}
      } else {
        cleanups.push(cleanup);
      }
      return cleanup;
    }

    function listen(target, type, handler, options) {
      if (!target || !target.addEventListener) return;
      target.addEventListener(type, handler, options);
      add(function () { target.removeEventListener(type, handler, options); });
    }

    function timeout(callback, delay) {
      const id = window.setTimeout(callback, delay);
      add(function () { window.clearTimeout(id); });
      return id;
    }

    function frame(callback) {
      const id = window.requestAnimationFrame(callback);
      add(function () { window.cancelAnimationFrame(id); });
      return id;
    }

    function destroy() {
      if (destroyed) return;
      destroyed = true;
      cleanups.splice(0).reverse().forEach(function (cleanup) {
        try { cleanup(); } catch (error) {}
      });
    }

    return {
      add: add,
      listen: listen,
      timeout: timeout,
      frame: frame,
      destroy: destroy,
      get destroyed() { return destroyed; }
    };
  }

  function queryAll(root, selector) {
    const results = [];
    if (!root) return results;
    if (root.nodeType === 1 && root.matches(selector)) results.push(root);
    if (root.querySelectorAll) results.push.apply(results, root.querySelectorAll(selector));
    return results;
  }

  function escapeHTML(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (character) {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      }[character];
    });
  }

  function safeHref(value, fallback) {
    const candidate = String(value || '').trim();
    if (!candidate) return fallback || '#';
    try {
      const parsed = new URL(candidate, window.location.href);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return fallback || '#';
      return parsed.origin === window.location.origin
        ? parsed.pathname + parsed.search + parsed.hash
        : parsed.href;
    } catch (error) {
      return fallback || '#';
    }
  }

  function safeImage(value) {
    const candidate = String(value || '').trim();
    if (!candidate) return '';
    try {
      const parsed = new URL(candidate, window.location.href);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:'
        ? parsed.href
        : '';
    } catch (error) {
      return '';
    }
  }

  function normalize(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function routeWithJSON(value, fallback) {
    const candidate = value || fallback || '';
    if (!candidate) return '';
    try {
      const parsed = new URL(candidate, window.location.href);
      if (parsed.origin === window.location.origin && !/\.js$/i.test(parsed.pathname)) {
        parsed.pathname = parsed.pathname.replace(/\/$/, '') + '.js';
      }
      return parsed.origin === window.location.origin
        ? parsed.pathname + parsed.search
        : parsed.href;
    } catch (error) {
      return candidate;
    }
  }

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    const amount = Number(cents || 0) / 100;
    const fixed = amount.toFixed(precision);
    const parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts[0] + (parts[1] ? decimal + parts[1] : '');
  }

  function formatMoney(cents, currency) {
    const moneyFormat = String(theme.moneyFormat || '');
    const currencyCode = String(currency || theme.currency || '').trim().toUpperCase();
    const shopCurrency = String(theme.shopCurrency || '').trim().toUpperCase();

    function finish(value) {
      const formatted = String(value || '');
      if (!theme.showCurrencyCode || !currencyCode || formatted.toUpperCase().indexOf(currencyCode) !== -1) return formatted;
      return formatted + ' ' + currencyCode;
    }

    if (moneyFormat.indexOf('{{') !== -1 && (!shopCurrency || currencyCode === shopCurrency)) {
      return finish(moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, token) {
        if (token === 'amount_no_decimals') return formatWithDelimiters(cents, 0, ',', '.');
        if (token === 'amount_with_comma_separator') return formatWithDelimiters(cents, 2, '.', ',');
        if (token === 'amount_no_decimals_with_comma_separator') return formatWithDelimiters(cents, 0, '.', ',');
        if (token === 'amount_with_apostrophe_separator') return formatWithDelimiters(cents, 2, "'", '.');
        if (token === 'amount_no_decimals_with_space_separator') return formatWithDelimiters(cents, 0, ' ', '.');
        if (token === 'amount_with_space_separator') return formatWithDelimiters(cents, 2, ' ', ',');
        return formatWithDelimiters(cents, 2, ',', '.');
      }));
    }
    try {
      return finish(new Intl.NumberFormat(theme.locale || undefined, {
        style: 'currency',
        currency: currencyCode || 'INR',
        currencyDisplay: theme.showCurrencyCode ? 'code' : 'symbol'
      }).format(Number(cents || 0) / 100));
    } catch (error) {
      return finish(formatWithDelimiters(cents, 2, ',', '.'));
    }
  }

  function requestMessage(payload, fallback) {
    if (payload && typeof payload === 'object') {
      const description = payload.description || payload.message || payload.error;
      if (typeof description === 'string' && description.trim()) return description.trim();
      if (description && typeof description === 'object') {
        const values = Object.keys(description).map(function (key) { return description[key]; }).flat();
        if (values.length) return values.join(' ');
      }
    }
    return fallback;
  }

  async function fetchJSON(url, options) {
    let loaderToken = null;
    const requestOptions = Object.assign({}, options || {});
    const externalSignal = requestOptions.signal || null;
    const timeoutMs = Math.max(4000, Number(requestOptions.requestTimeout || 18000) || 18000);
    const requestController = new AbortController();
    let timeoutId = 0;
    let timedOut = false;
    let abortFromExternal = null;

    delete requestOptions.requestTimeout;
    requestOptions.signal = requestController.signal;

    if (externalSignal) {
      abortFromExternal = function () { requestController.abort(); };
      if (externalSignal.aborted) requestController.abort();
      else externalSignal.addEventListener('abort', abortFromExternal, { once: true });
    }

    timeoutId = window.setTimeout(function () {
      timedOut = true;
      requestController.abort();
    }, timeoutMs);

    try {
      const requestURL = new URL(url, window.location.href);
      const method = String(requestOptions.method || 'GET').toUpperCase();
      const isCartJSON = /\/cart(?:\/|\.|$)/i.test(requestURL.pathname);
      const isCheckout = /\/checkout(?:\/|$)/i.test(requestURL.pathname);
      if (requestURL.origin === window.location.origin && !isCartJSON && (method !== 'GET' || isCheckout)) {
        if (window.PellairLoader && typeof window.PellairLoader.start === 'function') loaderToken = window.PellairLoader.start();
      }
    } catch (error) {}

    try {
      const response = await window.fetch(url, requestOptions);
      const text = await response.text();
      let payload = null;
      if (text) {
        try { payload = JSON.parse(text); } catch (error) {}
      }
      if (!response.ok) {
        const requestError = new Error(requestMessage(payload, 'The request could not be completed.'));
        requestError.status = response.status;
        requestError.payload = payload;
        throw requestError;
      }
      if (payload == null) throw new Error('The store returned an unreadable response.');
      return payload;
    } catch (error) {
      if (timedOut) {
        const timeoutError = new Error('The store took too long to respond. Please try again.');
        timeoutError.name = 'TimeoutError';
        throw timeoutError;
      }
      throw error;
    } finally {
      window.clearTimeout(timeoutId);
      if (externalSignal && abortFromExternal) externalSignal.removeEventListener('abort', abortFromExternal);
      if (loaderToken && window.PellairLoader && typeof window.PellairLoader.done === 'function') {
        window.PellairLoader.done(loaderToken);
      }
    }
  }

  function isAbortError(error) {
    return !!error && (error.name === 'AbortError' || error.code === 20);
  }

  function customerErrorMessage(error, fallback) {
    const message = String(error && error.message || '').trim();
    if (!message || (error && error.name === 'TypeError') || /failed to fetch|network\s*error|network unavailable|load failed/i.test(message)) {
      return fallback;
    }
    return message;
  }

  function mount(name, element, factory) {
    let map = controllerMaps.get(element);
    if (!map) {
      map = new Map();
      controllerMaps.set(element, map);
    }
    if (map.has(name)) return map.get(name).controller;

    try {
      const controller = factory(element) || {};
      const record = { name: name, element: element, controller: controller };
      map.set(name, record);
      controllerRecords.add(record);
      return controller;
    } catch (error) {
      element.classList.add('is-visible', 'is-revealed');
      if (window.console) console.error('[PellairTheme] ' + name + ' failed open.', error);
      return null;
    }
  }

  function destroyRecord(record) {
    if (!record || !controllerRecords.has(record)) return;
    controllerRecords.delete(record);
    const map = controllerMaps.get(record.element);
    if (map) map.delete(record.name);
    if (record.controller && typeof record.controller.destroy === 'function') {
      try { record.controller.destroy(); } catch (error) {}
    }
  }

  function containsOrIs(root, element) {
    if (!root || !element) return false;
    if (root === document || root === element) return true;
    return !!root.contains && root.contains(element);
  }

  function destroyWithin(root) {
    Array.from(controllerRecords).forEach(function (record) {
      if (containsOrIs(root, record.element)) destroyRecord(record);
    });
  }

  function invokeLifecycle(method, event) {
    Array.from(controllerRecords).forEach(function (record) {
      const controller = record.controller;
      if (controller && typeof controller[method] === 'function') {
        try { controller[method](event); } catch (error) {}
      }
    });
  }

  function createRevealManager() {
    const selector = '[data-reveal], .reveal-on-scroll';
    const observed = new Set();
    let observer = null;

    function reveal(element) {
      element.classList.add('is-visible', 'is-revealed');
      element.classList.remove('is-reveal-pending');
      if (observer) observer.unobserve(element);
      observed.delete(element);
      try { element.dispatchEvent(new CustomEvent('pellair:revealed', { bubbles: false })); } catch (error) {}
    }

    function revealWithin(root) {
      queryAll(root || document, selector).forEach(reveal);
    }

    if (!theme.designMode && !reduceMotion.matches && 'IntersectionObserver' in window) {
      try {
        observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) reveal(entry.target);
          });
        }, { threshold: 0.08, rootMargin: '0px 0px -5% 0px' });
        html.classList.add('motion-ready');
      } catch (error) {
        observer = null;
      }
    }

    function observeWithin(root) {
      const elements = queryAll(root || document, selector);
      if (!observer) {
        elements.forEach(reveal);
        return;
      }
      elements.forEach(function (element) {
        if (element.classList.contains('is-visible') || element.classList.contains('is-revealed')) return;
        element.classList.add('is-reveal-pending');
        observed.add(element);
        observer.observe(element);
      });
    }

    function removeWithin(root) {
      Array.from(observed).forEach(function (element) {
        if (containsOrIs(root, element)) {
          if (observer) observer.unobserve(element);
          observed.delete(element);
        }
      });
    }

    function destroy() {
      if (observer) observer.disconnect();
      observer = null;
      revealWithin(document);
      observed.clear();
      html.classList.remove('motion-ready');
    }

    return {
      observeWithin: observeWithin,
      revealWithin: revealWithin,
      removeWithin: removeWithin,
      destroy: destroy
    };
  }

  const reveals = createRevealManager();

  function createHeaderController(header) {
    const scope = createScope();
    const toggle = header.querySelector('[data-mobile-toggle]');
    const nav = header.querySelector('[data-mobile-nav]');
    const searchDetails = header.querySelector('.header-search-details');
    const mobileQuery = window.matchMedia ? window.matchMedia('(max-width: 1199px)') : null;
    let open = false;
    let scrollFrame = 0;

    function setOpen(next, options) {
      if (!toggle || !nav) return;
      open = !!next;
      nav.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      document.body.classList.toggle('mobile-nav-open', open);
      const label = toggle.querySelector('.visually-hidden');
      if (label) label.textContent = open ? 'Close menu' : 'Open menu';
      if (!open && options && options.restoreFocus) toggle.focus({ preventScroll: true });
    }

    if (toggle && nav) {
      if (!nav.id) nav.id = 'PellairNavigation-' + (++uid);
      toggle.setAttribute('aria-controls', nav.id);
      scope.listen(toggle, 'click', function () {
        if (searchDetails) searchDetails.open = false;
        setOpen(!open);
      });
      scope.listen(nav, 'click', function (event) {
        if (event.target.closest('a[href]')) setOpen(false);
      });
      if (searchDetails) {
        scope.listen(searchDetails, 'toggle', function () {
          if (searchDetails.open) setOpen(false);
        });
      }
      scope.listen(document, 'pellair:overlay:open', function (event) {
        if (event.detail && event.detail.source === 'header') return;
        setOpen(false);
        if (searchDetails) searchDetails.open = false;
      });
      scope.listen(document, 'click', function (event) {
        if (open && !event.target.closest('[data-header]')) setOpen(false);
        if (searchDetails && searchDetails.open && !event.target.closest('.header-search-details')) {
          searchDetails.open = false;
        }
      });
      scope.listen(document, 'keydown', function (event) {
        if (event.key !== 'Escape') return;
        if (open) {
          event.preventDefault();
          setOpen(false, { restoreFocus: true });
        } else if (searchDetails && searchDetails.open) {
          searchDetails.open = false;
          const summary = searchDetails.querySelector('summary');
          if (summary) summary.focus({ preventScroll: true });
        }
      });
      if (mobileQuery) {
        const handleMedia = function () { if (!mobileQuery.matches) setOpen(false); };
        if (mobileQuery.addEventListener) scope.listen(mobileQuery, 'change', handleMedia);
        else if (mobileQuery.addListener) {
          mobileQuery.addListener(handleMedia);
          scope.add(function () { mobileQuery.removeListener(handleMedia); });
        }
      }
    }

    function updateDepth() {
      scrollFrame = 0;
      header.classList.toggle('is-scrolled', window.scrollY > 24);
    }

    function requestDepth() {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(updateDepth);
    }

    scope.listen(window, 'scroll', requestDepth, { passive: true });
    scope.add(function () { if (scrollFrame) window.cancelAnimationFrame(scrollFrame); });
    updateDepth();

    return {
      pageHide: function () { setOpen(false); if (searchDetails) searchDetails.open = false; },
      editorSelect: function () { setOpen(false); if (searchDetails) searchDetails.open = false; },
      destroy: function () {
        setOpen(false);
        if (searchDetails) searchDetails.open = false;
        scope.destroy();
      }
    };
  }

  function createAssemblyController(root) {
    const scope = createScope();
    const layers = Array.from(root.querySelectorAll('[data-assembly-layer]'));
    const copy = Array.from(root.querySelectorAll('[data-assembly-copy]'));
    const isHome = theme.pageType === 'index' || document.body.classList.contains('template-index');
    const mobile = window.matchMedia ? window.matchMedia('(max-width: 749px)') : { matches: false };
    let started = false;
    let finished = false;
    let finishTimer = 0;

    layers.concat(copy).forEach(function (element, index) {
      element.style.setProperty('--assembly-index', String(index));
    });

    function finish(reason) {
      if (finished) return;
      finished = true;
      window.clearTimeout(finishTimer);
      root.classList.remove('is-assembly-ready', 'is-assembling');
      root.classList.add('is-assembled');
      layers.concat(copy).forEach(function (element) {
        element.classList.remove('is-assembly-pending');
      });
      try {
        root.dispatchEvent(new CustomEvent('pellair:assembly-complete', {
          bubbles: true,
          detail: { reason: reason || 'complete' }
        }));
      } catch (error) {}
    }

    function play(reason) {
      if (started || finished) return;
      started = true;
      if (!isHome || theme.designMode || reduceMotion.matches || !layers.length) {
        finish(reason || 'static');
        return;
      }
      root.classList.add('is-assembly-ready');
      layers.concat(copy).forEach(function (element) {
        element.classList.add('is-assembly-pending');
      });
      scope.frame(function () {
        scope.frame(function () {
          if (finished) return;
          root.classList.add('is-assembling');
          finishTimer = window.setTimeout(function () { finish('timeout'); }, mobile.matches ? 950 : 1450);
        });
      });
    }

    function introComplete(event) {
      play(event && event.detail && event.detail.reason || 'intro');
    }

    scope.listen(document, 'pellair:intro-complete', introComplete);
    scope.listen(document, 'pellair:home-reveal', introComplete);

    if (!isHome || theme.designMode || reduceMotion.matches) {
      finish('static');
    } else if (!html.classList.contains('intro-pending')) {
      scope.frame(function () { play('page-ready'); });
    }

    return {
      play: play,
      finish: finish,
      pageHide: function () { finish('pagehide'); },
      editorSelect: function () { finish('editor'); },
      destroy: function () {
        finish('destroy');
        scope.destroy();
      }
    };
  }

  function createCartDrawerController(root) {
    const scope = createScope();
    const panel = root.querySelector('[data-cart-drawer-panel]') || root.querySelector('.cart-drawer__panel');
    const body = root.querySelector('[data-cart-drawer-body]');
    const status = root.querySelector('[data-cart-status]');
    const cartUrl = routeWithJSON(routes.cart, '/cart');
    const addUrl = routeWithJSON(routes.cartAdd, '/cart/add');
    const changeUrl = routeWithJSON(routes.cartChange, '/cart/change');
    const updateUrl = routeWithJSON(routes.cartUpdate, '/cart/update');
    const activeRequests = new Set();
    let refreshController = null;
    let changePending = false;
    let currentCart = null;
    let cartPairIssue = '';
    let opener = null;
    let hideTimer = 0;
    let recoveryTimer = 0;
    let isOpen = false;
    let pageLocked = false;
    let bodyOverflow = '';
    let bodyPaddingRight = '';

    root.inert = true;

    function itemProperties(item) {
      return item && item.properties && typeof item.properties === 'object' ? item.properties : {};
    }

    function finishedBatch(item) {
      return String(itemProperties(item)['_Finished perfume batch'] || '').trim();
    }

    function finishedRole(item) {
      const properties = itemProperties(item);
      const explicit = String(properties['_Finished perfume role'] || '').trim().toLowerCase();
      if (explicit === 'oil' || explicit === 'service') return explicit;
      return String(properties['_Oil variant'] || '').trim() ? 'service' : 'oil';
    }

    function isFinishedServiceProduct(item) {
      const properties = itemProperties(item);
      const handle = String(item && (item.handle || item.product_handle) || '').trim().toLowerCase();
      const url = String(item && item.url || '').toLowerCase();
      return String(properties['_Finished perfume role'] || '').trim().toLowerCase() === 'service' ||
        !!String(properties['_Oil variant'] || '').trim() ||
        handle === 'finished-perfume-preparation' ||
        url.indexOf('/products/finished-perfume-preparation') !== -1;
    }

    function finishedRatio(items) {
      const explicitRatios = items.map(function (item) {
        return Number(itemProperties(item)['_Finished perfume units per oil'] || 0);
      }).filter(function (ratio) { return Number.isInteger(ratio) && ratio > 0; });
      if (explicitRatios.length) {
        const ratio = explicitRatios[0];
        if (explicitRatios.some(function (value) { return value !== ratio; })) return 0;
        return ratio;
      }

      const oil = items.find(function (item) { return finishedRole(item) === 'oil'; });
      const service = items.find(function (item) { return finishedRole(item) === 'service'; });
      const serviceVariantTitle = String(service && service.variant_title || '');
      if (/(?:^|\s|\/)(?:100\s*(?:g|gm)|1\s*kg|5\s*kg)(?:\s|\/|$)/i.test(serviceVariantTitle)) return 1;
      const oilQuantity = Number(oil && oil.quantity || 0);
      const serviceQuantity = Number(service && service.quantity || 0);
      const inferred = oilQuantity > 0 ? serviceQuantity / oilQuantity : 0;
      return Number.isInteger(inferred) && inferred > 0 ? inferred : 0;
    }

    function inspectFinishedPair(item, cart) {
      const batch = finishedBatch(item);
      if (!batch) return { batch: '', items: [], valid: true, ratio: 1 };
      const cartItems = cart && Array.isArray(cart.items) ? cart.items : [];
      const items = cartItems.filter(function (candidate) { return finishedBatch(candidate) === batch; });
      const oilItems = items.filter(function (candidate) { return finishedRole(candidate) === 'oil'; });
      const serviceItems = items.filter(function (candidate) { return finishedRole(candidate) === 'service'; });
      const ratio = finishedRatio(items);
      const oil = oilItems[0] || null;
      const service = serviceItems[0] || null;
      const linkedVariant = String(itemProperties(service)['_Oil variant'] || '').trim();
      const oilVariant = String(oil && (oil.variant_id || oil.id) || '').trim();
      const linkedVariantMatches = !linkedVariant || !oilVariant || linkedVariant === oilVariant;
      const quantitiesMatch = !!(oil && service && ratio && Number(service.quantity || 0) === Number(oil.quantity || 0) * ratio);
      const valid = items.length === 2 && oilItems.length === 1 && serviceItems.length === 1 &&
        items.every(function (candidate) { return !!candidate.key && Number(candidate.quantity || 0) > 0; }) &&
        linkedVariantMatches && quantitiesMatch;
      return {
        batch: batch,
        items: items,
        oil: oil,
        service: service,
        ratio: ratio,
        valid: valid
      };
    }

    function finishedCartIssue(cart) {
      const items = cart && Array.isArray(cart.items) ? cart.items : [];
      const seen = new Set();
      for (let index = 0; index < items.length; index += 1) {
        const batch = finishedBatch(items[index]);
        if (isFinishedServiceProduct(items[index]) && !batch) {
          return 'A perfume-preparation item is not linked to an oil. Remove it and add perfume preparation from an oil product before checkout.';
        }
        if (!batch || seen.has(batch)) continue;
        seen.add(batch);
        if (!inspectFinishedPair(items[index], cart).valid) {
          return 'A finished-perfume set is incomplete. Remove it and add the perfume preparation option again before checkout.';
        }
      }
      return '';
    }

    function announce(message, error) {
      if (!status) return;
      status.textContent = message || '';
      status.hidden = !message;
      status.setAttribute('role', error ? 'alert' : 'status');
      status.setAttribute('aria-live', error ? 'assertive' : 'polite');
      status.classList.toggle('is-error', !!error);
      status.dataset.state = error ? 'error' : (message ? 'status' : 'idle');
      if (error) status.classList.remove('visually-hidden');
      else status.classList.add('visually-hidden');
    }

    function setCounts(count) {
      document.querySelectorAll('[data-cart-count]').forEach(function (element) {
        element.textContent = String(Number(count || 0));
        const trigger = element.closest('[data-cart-open]');
        if (trigger) trigger.setAttribute('aria-label', 'Cart, ' + count + (Number(count) === 1 ? ' item' : ' items'));
      });
    }

    function renderShipbar(cart) {
      const bar = root.querySelector('[data-cart-shipbar]');
      if (!bar) return;
      const cartCurrency = String(cart && cart.currency || theme.currency || '').trim().toUpperCase();
      const shopCurrency = String(theme.shopCurrency || '').trim().toUpperCase();
      if (shopCurrency && cartCurrency && cartCurrency !== shopCurrency) {
        bar.hidden = true;
        return;
      }
      const explicit = Number(root.getAttribute('data-free-shipping-threshold-cents') || 0);
      const legacy = Number(root.getAttribute('data-free-shipping-threshold') || 0);
      const threshold = explicit || legacy * 100;
      if (!threshold || !cart.items || !cart.items.length) {
        bar.hidden = true;
        return;
      }
      const remaining = Math.max(0, threshold - Number(cart.total_price || 0));
      const progress = Math.min(1, Number(cart.total_price || 0) / threshold);
      const fill = bar.querySelector('[data-cart-shipbar-fill]');
      const label = bar.querySelector('[data-cart-shipbar-label]');
      bar.hidden = false;
      bar.classList.toggle('is-unlocked', remaining <= 0);
      if (fill) fill.style.transform = 'scaleX(' + progress.toFixed(4) + ')';
      if (label) label.textContent = remaining > 0
        ? formatMoney(remaining, cart.currency) + ' away from free shipping'
        : 'Free shipping unlocked';
    }

    function propertyHTML(item) {
      const properties = item && item.properties && typeof item.properties === 'object'
        ? Object.keys(item.properties).filter(function (name) {
            const value = item.properties[name];
            return name.charAt(0) !== '_' && value != null && String(value).trim() !== '';
          })
        : [];
      if (!properties.length) return '';
      return '<dl class="cart-drawer-item__properties">' + properties.map(function (name) {
        return '<div><dt>' + escapeHTML(name) + '</dt><dd>' + escapeHTML(item.properties[name]) + '</dd></div>';
      }).join('') + '</dl>';
    }

    function discountHTML(item, currency) {
      const allocations = Array.isArray(item.line_level_discount_allocations)
        ? item.line_level_discount_allocations
        : [];
      if (!allocations.length) return '';
      return '<ul class="cart-drawer-item__discounts">' + allocations.map(function (allocation) {
        const title = allocation.discount_application && allocation.discount_application.title
          ? allocation.discount_application.title
          : 'Discount';
        return '<li>' + escapeHTML(title) + ' −' + escapeHTML(formatMoney(allocation.amount, currency)) + '</li>';
      }).join('') + '</ul>';
    }

    function itemHTML(item, index, currency) {
      const key = String(item.key || '');
      const line = index + 1;
      const quantity = Number(item.quantity || 0);
      const productTitle = item.product_title || item.title || 'Oil';
      const href = safeHref(item.url, routes.allProducts || '/collections/all');
      const image = safeImage(item.image);
      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? '<p class="cart-drawer-item__variant">' + escapeHTML(item.variant_title) + '</p>'
        : '';
      const sellingPlan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
        ? '<p class="cart-drawer-item__plan">' + escapeHTML(item.selling_plan_allocation.selling_plan.name) + '</p>'
        : '';
      const original = Number(item.original_line_price || 0);
      const finalPrice = Number(item.final_line_price || 0);
      const price = original > finalPrice
        ? '<span class="cart-drawer-item__price"><s>' + escapeHTML(formatMoney(original, currency)) + '</s> ' + escapeHTML(formatMoney(finalPrice, currency)) + '</span>'
        : '<span class="cart-drawer-item__price">' + escapeHTML(formatMoney(finalPrice, currency)) + '</span>';
      const imageMarkup = image
        ? '<img src="' + escapeHTML(image) + '" alt="" width="120" height="120" loading="lazy" decoding="async">'
        : '<span class="cart-drawer-item__image-placeholder" aria-hidden="true">PO</span>';
      return '<article class="cart-drawer-item" data-cart-item data-cart-line="' + line + '">' +
        '<a class="cart-drawer-item__image" href="' + escapeHTML(href) + '" aria-label="View ' + escapeHTML(productTitle) + '">' + imageMarkup + '</a>' +
        '<div class="cart-drawer-item__info">' +
          '<h3><a href="' + escapeHTML(href) + '">' + escapeHTML(productTitle) + '</a></h3>' +
          variant + sellingPlan + propertyHTML(item) + discountHTML(item, currency) +
          '<div class="cart-drawer-item__row">' +
            '<div class="cart-drawer-item__qty" role="group" aria-label="Quantity for ' + escapeHTML(productTitle) + '">' +
              '<button type="button" data-cart-item-key="' + escapeHTML(key) + '" data-cart-line-number="' + line + '" data-cart-quantity="' + Math.max(0, quantity - 1) + '" aria-label="Decrease ' + escapeHTML(productTitle) + ' quantity">−</button>' +
              '<span aria-label="Current quantity: ' + quantity + '">' + quantity + '</span>' +
              '<button type="button" data-cart-item-key="' + escapeHTML(key) + '" data-cart-line-number="' + line + '" data-cart-quantity="' + (quantity + 1) + '" aria-label="Increase ' + escapeHTML(productTitle) + ' quantity">+</button>' +
            '</div>' + price +
          '</div>' +
          '<button type="button" class="plain cart-drawer-item__remove" data-cart-item-key="' + escapeHTML(key) + '" data-cart-line-number="' + line + '" data-cart-quantity="0" aria-label="Remove ' + escapeHTML(productTitle) + ' from cart">Remove</button>' +
        '</div>' +
      '</article>';
    }

    function renderErrorState(message) {
      if (!body) return;
      body.setAttribute('aria-busy', 'false');
      body.innerHTML = '<div class="cart-drawer__error" role="group" aria-label="Cart unavailable">' +
        '<p>' + escapeHTML(message) + '</p>' +
        '<div class="cart-drawer__error-actions">' +
          '<button class="button button--small" type="button" data-cart-retry>Try again</button>' +
          '<a class="button button--ghost button--small" href="' + escapeHTML(safeHref(routes.cart, '/cart')) + '">Open cart page</a>' +
        '</div></div>';
    }

    function render(cart) {
      currentCart = cart || null;
      cartPairIssue = finishedCartIssue(cart);
      if (!body) return;
      const items = Array.isArray(cart.items) ? cart.items : [];
      setCounts(cart.item_count);
      renderShipbar(cart);

      const subtotal = root.querySelector('[data-cart-subtotal]');
      if (subtotal) {
        subtotal.hidden = !items.length;
        const amount = subtotal.querySelector('[data-cart-subtotal-amount]');
        if (amount) amount.textContent = formatMoney(cart.total_price, cart.currency);
      }

      if (!items.length) {
        body.innerHTML = '<div class="empty-state cart-drawer__empty"><p>Your cart is empty.</p><a class="button button--small" href="' +
          escapeHTML(safeHref(routes.allProducts, '/collections/all')) + '" data-cart-empty-focus>Explore the oils</a></div>';
      } else {
        body.innerHTML = items.map(function (item, index) {
          return itemHTML(item, index, cart.currency);
        }).join('');
      }
      body.setAttribute('aria-busy', 'false');
      try {
        document.dispatchEvent(new CustomEvent('pellair:cart-updated', { detail: { cart: cart } }));
      } catch (error) {}
    }

    function abortRefresh() {
      if (!refreshController) return;
      const pendingRefresh = refreshController;
      pendingRefresh.abort();
      if (refreshController === pendingRefresh) refreshController = null;
    }

    async function refresh(options) {
      const settings = options || {};
      if (!cartUrl || !body) return null;
      abortRefresh();
      const controller = new AbortController();
      refreshController = controller;
      if (!settings.silent) {
        body.setAttribute('aria-busy', 'true');
        announce('Loading your cart.');
      }
      try {
        const cart = await fetchJSON(cartUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: controller.signal
        });
        render(cart);
        if (!settings.silent) announce(cartPairIssue || 'Cart updated.', !!cartPairIssue);
        return cart;
      } catch (error) {
        if (isAbortError(error)) return null;
        body.setAttribute('aria-busy', 'false');
        const message = customerErrorMessage(error, 'Your cart could not be loaded. Try again.');
        renderErrorState(message);
        announce(message, true);
        throw error;
      } finally {
        if (refreshController === controller) refreshController = null;
      }
    }

    function lockPage() {
      if (pageLocked) return;
      pageLocked = true;
      bodyOverflow = document.body.style.overflow;
      bodyPaddingRight = document.body.style.paddingRight;
      const scrollbar = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      document.body.style.overflow = 'hidden';
      if (scrollbar) document.body.style.paddingRight = scrollbar + 'px';
    }

    function unlockPage() {
      if (!pageLocked) return;
      pageLocked = false;
      document.body.style.overflow = bodyOverflow;
      document.body.style.paddingRight = bodyPaddingRight;
      bodyOverflow = '';
      bodyPaddingRight = '';
    }

    function focusableElements() {
      if (!panel) return [];
      return Array.from(panel.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')).filter(function (element) {
        return !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.offsetParent !== null;
      });
    }

    function open(trigger, options) {
      if (!panel || !body || !root.isConnected || !panel.isConnected || !body.isConnected) {
        unlockPage();
        return false;
      }
      window.clearTimeout(hideTimer);
      window.clearTimeout(recoveryTimer);
      document.dispatchEvent(new CustomEvent('pellair:overlay:open', { detail: { source: 'cart' } }));
      opener = trigger || document.activeElement;
      isOpen = true;
      root.hidden = false;
      root.inert = false;
      root.setAttribute('aria-hidden', 'false');
      root.classList.add('is-open');
      html.classList.add('cart-drawer-is-open');
      document.body.classList.add('cart-drawer-is-open');
      lockPage();
      scope.frame(function () {
        const closeButton = panel.querySelector('[data-cart-close]');
        try { (closeButton || panel).focus({ preventScroll: true }); } catch (error) {}
      });
      recoveryTimer = window.setTimeout(function () {
        if (!isOpen) return;
        const visible = root.isConnected && panel.isConnected && !root.hidden && root.classList.contains('is-open') && window.getComputedStyle(root).visibility !== 'hidden';
        if (!visible) close({ restoreFocus: false, immediate: true });
      }, 900);
      if ((!options || options.refresh !== false) && !changePending) refresh().catch(function () {});
      return true;
    }

    function close(options) {
      const settings = options || {};
      const wasOpen = isOpen || !root.hidden || root.classList.contains('is-open') || pageLocked;
      isOpen = false;
      root.classList.remove('is-open');
      root.setAttribute('aria-hidden', 'true');
      root.inert = true;
      html.classList.remove('cart-drawer-is-open');
      document.body.classList.remove('cart-drawer-is-open');
      unlockPage();
      window.clearTimeout(hideTimer);
      window.clearTimeout(recoveryTimer);
      if (settings.immediate || reduceMotion.matches) root.hidden = true;
      else hideTimer = window.setTimeout(function () { if (!isOpen) root.hidden = true; }, 320);
      if (wasOpen && settings.restoreFocus !== false) {
        const canFocus = function (element) {
          return !!(element && element.isConnected && element.focus && !element.disabled && !element.inert && !element.closest('[inert]'));
        };
        let focusTarget = canFocus(opener) ? opener : null;
        if (!focusTarget) {
          focusTarget = Array.from(document.querySelectorAll('[data-cart-open]')).find(canFocus) || document.getElementById('MainContent');
        }
        if (canFocus(focusTarget)) focusTarget.focus({ preventScroll: true });
      }
      opener = null;
    }

    function makeRequestController(externalSignal) {
      const controller = new AbortController();
      activeRequests.add(controller);
      let abortFromExternal = null;
      if (externalSignal) {
        abortFromExternal = function () { controller.abort(); };
        if (externalSignal.aborted) controller.abort();
        else externalSignal.addEventListener('abort', abortFromExternal, { once: true });
      }
      return {
        controller: controller,
        release: function () {
          activeRequests.delete(controller);
          if (externalSignal && abortFromExternal) externalSignal.removeEventListener('abort', abortFromExternal);
        }
      };
    }

    async function add(formData, trigger, externalSignal) {
      if (!addUrl) throw new Error('Cart is unavailable right now.');
      const request = makeRequestController(externalSignal);
      try {
        await fetchJSON(addUrl, {
          method: 'POST',
          body: formData,
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: request.controller.signal
        });

        open(trigger, { refresh: false });
        announce('Added. Updating your cart.');
        try {
          const cart = await refresh();
          if (cart) {
            announce(cartPairIssue || 'Added to your cart.', !!cartPairIssue);
            return { added: true, refreshed: true, cart: cart };
          }
          const message = 'Item added, but cart details could not refresh. View your cart to confirm.';
          announce(message, true);
          return { added: true, refreshed: false, cart: null, message: message };
        } catch (error) {
          const message = 'Item added, but cart details could not refresh. View your cart to confirm.';
          announce(message, true);
          return { added: true, refreshed: false, cart: null, message: message };
        }
      } finally {
        request.release();
      }
    }

    async function addItems(items, trigger, externalSignal) {
      if (!addUrl) throw new Error('Cart is unavailable right now.');
      if (!Array.isArray(items) || !items.length) throw new Error('No configured items were provided.');
      const request = makeRequestController(externalSignal);
      try {
        await fetchJSON(addUrl, {
          method: 'POST',
          body: JSON.stringify({ items: items }),
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          signal: request.controller.signal
        });

        open(trigger, { refresh: false });
        announce('Configured order added. Updating your cart.');
        try {
          const cart = await refresh();
          if (cart) {
            announce(cartPairIssue || 'Configured order added to your cart.', !!cartPairIssue);
            return { added: true, refreshed: true, cart: cart };
          }
          const message = 'Your configured order was added, but cart details could not refresh. View your cart to confirm.';
          announce(message, true);
          return { added: true, refreshed: false, cart: null, message: message };
        } catch (error) {
          const message = 'Your configured order was added, but cart details could not refresh. View your cart to confirm.';
          announce(message, true);
          return { added: true, refreshed: false, cart: null, message: message };
        }
      } finally {
        request.release();
      }
    }

    function focusAfterCartChange(key, line, action, removed) {
      scope.frame(function () {
        if (!isOpen || root.hidden || root.inert) return;
        if (!body) return;
        const rows = Array.from(body.querySelectorAll('[data-cart-item], .cart-drawer-item'));
        if (removed) {
          const adjacent = rows.length ? rows[Math.min(Math.max(0, line - 1), rows.length - 1)] : null;
          const adjacentControl = adjacent && adjacent.querySelector('[data-cart-quantity], .cart-drawer-item__remove, a[href]');
          const emptyAction = body.querySelector('[data-cart-empty-focus]');
          const closeButton = panel && panel.querySelector('[data-cart-close]');
          const fallback = adjacentControl || emptyAction || body || closeButton || panel;
          if (fallback) fallback.focus({ preventScroll: true });
          return;
        }

        const replacement = rows.find(function (candidate) {
          return Array.from(candidate.querySelectorAll('[data-cart-item-key], [data-cart-line-number]')).some(function (control) {
            return (key && control.getAttribute('data-cart-item-key') === key) ||
              (!key && line && Number(control.getAttribute('data-cart-line-number') || 0) === line);
          });
        });
        let target = null;
        if (replacement) {
          target = Array.from(replacement.querySelectorAll('button')).find(function (control) {
            if (action === 'remove') return control.classList.contains('cart-drawer-item__remove');
            return String(control.getAttribute('aria-label') || '').toLowerCase().indexOf(action) === 0;
          }) || replacement.querySelector('button, a[href]');
        }
        const closeButton = panel && panel.querySelector('[data-cart-close]');
        const fallback = target || closeButton || panel;
        if (fallback) fallback.focus({ preventScroll: true });
      });
    }

    async function changeQuantity(button) {
      if (!changeUrl || button.disabled || changePending) return;
      changePending = true;
      const row = button.closest('[data-cart-item], .cart-drawer-item');
      const quantity = Math.max(0, Number(button.getAttribute('data-cart-quantity') || 0));
      const key = button.getAttribute('data-cart-item-key') || '';
      const line = Number(button.getAttribute('data-cart-line-number') || 0);
      const label = String(button.getAttribute('aria-label') || '').toLowerCase();
      const action = button.classList.contains('cart-drawer-item__remove')
        ? 'remove'
        : (label.indexOf('decrease') === 0 ? 'decrease' : 'increase');
      if (!key && !line) {
        changePending = false;
        return;
      }

      const cartItems = currentCart && Array.isArray(currentCart.items) ? currentCart.items : [];
      const item = key
        ? cartItems.find(function (candidate) { return String(candidate.key || '') === key; })
        : cartItems[line - 1];
      const pair = item ? inspectFinishedPair(item, currentCart) : null;
      const isPaired = !!(pair && pair.batch);
      let requestUrl = changeUrl;
      let payload = key ? { id: key, quantity: quantity } : { line: line, quantity: quantity };
      let resultingQuantity = quantity;

      if (isPaired) {
        if (!updateUrl) {
          announce('This configured set can only be changed from the cart page.', true);
          changePending = false;
          return;
        }

        const updates = {};
        if (quantity === 0) {
          pair.items.forEach(function (candidate) {
            if (candidate.key) updates[candidate.key] = 0;
          });
          resultingQuantity = 0;
        } else {
          if (!pair.valid) {
            announce('This finished-perfume set is incomplete. Remove it and add the perfume preparation option again.', true);
            changePending = false;
            return;
          }
          let oilQuantity = quantity;
          if (finishedRole(item) === 'service') {
            const currentOilQuantity = Number(pair.oil && pair.oil.quantity || 0);
            oilQuantity = Math.max(0, currentOilQuantity + (action === 'decrease' ? -1 : 1));
          }
          updates[pair.oil.key] = oilQuantity;
          updates[pair.service.key] = oilQuantity * pair.ratio;
          resultingQuantity = oilQuantity;
        }
        requestUrl = updateUrl;
        payload = { updates: updates };
      }

      abortRefresh();
      const controls = Array.from(root.querySelectorAll('[data-cart-quantity]'));
      if (!controls.length) controls.push(button);
      controls.forEach(function (control) { control.disabled = true; });
      if (row) row.setAttribute('aria-busy', 'true');
      announce(resultingQuantity === 0
        ? (isPaired ? 'Removing the configured set.' : 'Removing item.')
        : (isPaired ? 'Updating both items in the configured set.' : 'Updating quantity.'));
      const request = makeRequestController();
      try {
        const cart = await fetchJSON(requestUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
          signal: request.controller.signal
        });
        abortRefresh();
        render(cart);
        focusAfterCartChange(key, line, action, resultingQuantity === 0);
        announce(cartPairIssue || (resultingQuantity === 0
          ? (isPaired ? 'Configured set removed.' : 'Item removed.')
          : (isPaired ? 'Configured set quantity updated.' : 'Quantity updated.')), !!cartPairIssue);
      } catch (error) {
        if (!isAbortError(error)) announce(customerErrorMessage(error, 'The quantity could not be updated. Try again.'), true);
        controls.forEach(function (control) { control.disabled = false; });
        if (row) row.setAttribute('aria-busy', 'false');
      } finally {
        changePending = false;
        controls.forEach(function (control) {
          if (control.isConnected) control.disabled = false;
        });
        request.release();
      }
    }

    scope.listen(document, 'click', function (event) {
      const trigger = event.target.closest('[data-cart-open]');
      if (trigger && !event.defaultPrevented) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        if (!open(trigger)) window.location.assign(safeHref(routes.cart, '/cart'));
        return;
      }
      const closeButton = event.target.closest('[data-cart-close]');
      if (closeButton && root.contains(closeButton)) {
        event.preventDefault();
        close();
        return;
      }
      const retryButton = event.target.closest('[data-cart-retry]');
      if (retryButton && root.contains(retryButton)) {
        event.preventDefault();
        retryButton.disabled = true;
        refresh().then(function () {
          const focusTarget = body && body.querySelector('[data-cart-item] a[href], [data-cart-empty-focus]');
          if (focusTarget) focusTarget.focus({ preventScroll: true });
        }).catch(function () {
          const replacement = body && body.querySelector('[data-cart-retry]');
          if (replacement) replacement.focus({ preventScroll: true });
        });
        return;
      }
      const quantityButton = event.target.closest('[data-cart-quantity]');
      if (quantityButton && root.contains(quantityButton)) {
        event.preventDefault();
        changeQuantity(quantityButton);
        return;
      }
      const legacy = event.target.closest('[data-drawer-qty]');
      if (legacy && root.contains(legacy)) {
        event.preventDefault();
        const values = String(legacy.getAttribute('data-drawer-qty') || '').split(':');
        legacy.setAttribute('data-cart-line-number', values[0] || '');
        legacy.setAttribute('data-cart-quantity', values[1] || '0');
        changeQuantity(legacy);
      }
    });

    scope.listen(document, 'keydown', function (event) {
      if (!isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = focusableElements();
      if (!focusable.length) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!panel.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    const controller = {
      add: add,
      addItems: addItems,
      open: open,
      close: close,
      refresh: refresh,
      pageHide: function () {
        close({ restoreFocus: false, immediate: true });
        if (refreshController) refreshController.abort();
        activeRequests.forEach(function (requestController) { requestController.abort(); });
      },
      pageShow: function () { refresh({ silent: true }).catch(function () {}); },
      destroy: function () {
        close({ restoreFocus: false, immediate: true });
        if (refreshController) refreshController.abort();
        activeRequests.forEach(function (controller) { controller.abort(); });
        activeRequests.clear();
        window.clearTimeout(hideTimer);
        window.clearTimeout(recoveryTimer);
        scope.destroy();
        if (theme.cart === controller) theme.cart = null;
      }
    };
    theme.cart = controller;
    return controller;
  }

  function createFinishedPerfumeConfigurator(form) {
    const root = form.querySelector('[data-finished-perfume]');
    if (!root) return null;

    const configured = root.getAttribute('data-finished-perfume-configured') === 'true';
    const toggle = root.querySelector('[data-finished-perfume-toggle]');
    const optionsFieldset = root.querySelector('[data-finished-perfume-options]');
    const serviceData = root.querySelector('[data-finished-perfume-services]');
    const result = root.querySelector('[data-finished-perfume-result]');
    const yieldTarget = root.querySelector('[data-finished-perfume-yield]');
    const priceTarget = root.querySelector('[data-finished-perfume-price]');
    const oilPriceTarget = root.querySelector('[data-finished-perfume-oil-price]');
    const totalPriceTarget = root.querySelector('[data-finished-perfume-total-price]');
    const quantityTarget = root.querySelector('[data-finished-perfume-quantity-input]');
    const quantityMinus = root.querySelector('[data-finished-perfume-quantity-minus]');
    const quantityPlus = root.querySelector('[data-finished-perfume-quantity-plus]');
    const sizeLabelTarget = root.querySelector('[data-finished-perfume-size-label]');
    const messageTarget = root.querySelector('[data-finished-perfume-message]');
    const contactLink = root.querySelector('[data-finished-perfume-contact]');
    const summaryTarget = root.querySelector('[data-finished-perfume-summary]');
    const stateTarget = root.querySelector('[data-finished-perfume-state]');
    const removeButton = root.querySelector('[data-finished-perfume-remove]');
    const sizeButtons = Array.from(root.querySelectorAll('[data-finished-perfume-size]'));
    const quantityInput = form.querySelector('input[name="quantity"]');
    const scope = createScope();
    const concentrationMeta = {
      '50': { label: '50% Rich', shortLabel: 'Rich', multiplier: 2, blend: '50% oil / 50% alcohol', unitPrice: 19900 },
      '33': { label: '33% Balanced', shortLabel: 'Balanced', multiplier: 3, blend: '33% oil / 67% alcohol', unitPrice: 29900 },
      '25': { label: '25% Light', shortLabel: 'Light', multiplier: 4, blend: '25% oil / 75% alcohol', unitPrice: 49900 }
    };
    let services = [];
    let currentVariant = null;
    let currentOilUnitPrice = 0;
    let lastEventState = '';

    function formatCompactPreparationMoney(cents) {
      return formatMoney(cents, theme.currency).replace(/([.,]00)(?=\s*$)/, '');
    }

    if (!toggle || !optionsFieldset || !serviceData) {
      return {
        isActive: function () { return false; },
        isRequested: function () { return false; },
        sync: function () {},
        items: function () { return []; },
        destroy: scope.destroy
      };
    }

    try {
      services = JSON.parse(serviceData.textContent.trim() || '[]');
      if (!Array.isArray(services)) services = [];
    } catch (error) {
      services = [];
    }

    function concentrationFromTitle(title) {
      const value = String(title || '').toLowerCase();
      if (/(^|\D)50(?:\.0)?\s*%?(\D|$)/.test(value) || value.indexOf('extrait') !== -1) return '50';
      if (/(^|\D)33(?:\.3+)?\s*%?(\D|$)/.test(value) || (value.indexOf('parfum') !== -1 && value.indexOf('eau') === -1)) return '33';
      if (/(^|\D)25(?:\.0)?\s*%?(\D|$)/.test(value) || value.indexOf('eau de parfum') !== -1) return '25';
      return '';
    }

    function gramsFromText(text) {
      const match = String(text || '').match(/(\d+(?:\.\d+)?)\s*(kg|kilograms?|kilos?|gm|grams?|g)\b/i);
      if (!match) return 0;
      const amount = Number(match[1]);
      if (!Number.isFinite(amount) || amount <= 0) return 0;
      return /^k/i.test(match[2]) ? amount * 1000 : amount;
    }

    function variantSizeGrams(variant) {
      if (!variant) return 0;
      const values = [];
      if (Array.isArray(variant.options)) values.push.apply(values, variant.options);
      if (variant.title) values.push(variant.title);
      return gramsFromText(values.join(' / '));
    }

    function sizeKeyFromGrams(grams) {
      if (Math.abs(Number(grams) - 100) < 0.01) return '100g';
      if (Math.abs(Number(grams) - 1000) < 0.01) return '1kg';
      if (Math.abs(Number(grams) - 5000) < 0.01) return '5kg';
      return '';
    }

    const exactServices = {};
    const unitServices = {};
    services.forEach(function (service) {
      const title = service && service.title;
      const concentration = concentrationFromTitle(title);
      if (!concentration) return;
      const sizeKey = sizeKeyFromGrams(gramsFromText(title));
      if (sizeKey && !exactServices[sizeKey + ':' + concentration]) {
        exactServices[sizeKey + ':' + concentration] = service;
      } else if (!sizeKey && !unitServices[concentration]) {
        unitServices[concentration] = service;
      }
    });

    const concentrationInputs = Array.from(root.querySelectorAll('input[name="finished_perfume_concentration"]'));

    function productSizeInput(sizeKey) {
      return Array.from(form.querySelectorAll('[data-option-value]')).find(function (input) {
        return sizeKeyFromGrams(gramsFromText(input.value)) === sizeKey;
      }) || null;
    }

    function syncSizeButtons(sizeKey) {
      sizeButtons.forEach(function (button) {
        const targetKey = button.getAttribute('data-finished-perfume-size') || '';
        const sourceInput = productSizeInput(targetKey);
        const sourcePill = sourceInput && sourceInput.closest('.option-pill');
        const unavailable = !sourceInput || sourceInput.disabled || !!(sourcePill && sourcePill.classList.contains('is-unavailable'));
        const selected = targetKey === sizeKey;
        button.disabled = unavailable;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
    }

    function selectedConcentration() {
      const selected = concentrationInputs.find(function (input) { return input.checked && !input.disabled; });
      return selected ? String(selected.value) : '';
    }

    function productQuantity() {
      const fallback = quantityInput && Number(quantityInput.min || 1) || 1;
      const amount = quantityInput && Number(quantityInput.value) || fallback;
      return Math.max(1, Math.round(amount));
    }

    function serviceSelection(concentration, gramsPerUnit, oilQuantity) {
      const meta = concentrationMeta[concentration];
      const sizeKey = sizeKeyFromGrams(gramsPerUnit);
      const preparationUnits = sizeKey ? Math.round(gramsPerUnit / 100) * oilQuantity : 0;
      if (!meta || !sizeKey || !preparationUnits) return null;

      const exactService = exactServices[sizeKey + ':' + concentration];
      const unitService = unitServices[concentration];
      const candidates = [];
      if (exactService) candidates.push({ service: exactService, quantity: oilQuantity, mode: 'size' });
      if (unitService) candidates.push({ service: unitService, quantity: preparationUnits, mode: 'unit' });

      const expectedTotal = meta.unitPrice * preparationUnits;
      const available = candidates.filter(function (candidate) {
        return candidate.service && candidate.service.available !== false && candidate.service.id;
      });
      const matching = available.find(function (candidate) {
        return Math.round(Number(candidate.service.price || 0) * candidate.quantity) === expectedTotal;
      });
      const selected = matching || available[0] || null;
      if (!selected) return null;

      return {
        service: selected.service,
        serviceQuantity: selected.quantity,
        mode: selected.mode,
        preparationUnits: preparationUnits,
        expectedTotal: expectedTotal,
        actualTotal: Math.round(Number(selected.service.price || 0) * selected.quantity),
        priceMatches: !!matching
      };
    }

    function snapshot(concentrationOverride) {
      const concentration = concentrationOverride || selectedConcentration();
      const meta = concentrationMeta[concentration];
      const gramsPerUnit = variantSizeGrams(currentVariant);
      const oilQuantity = productQuantity();
      const bottles = meta && gramsPerUnit
        ? Math.max(1, Math.round((gramsPerUnit / 100) * meta.multiplier * oilQuantity))
        : 0;
      const selection = serviceSelection(concentration, gramsPerUnit, oilQuantity);
      const previewValid = !!(meta && gramsPerUnit && bottles);
      const oilSubtotal = Math.max(0, Number(currentOilUnitPrice || currentVariant && currentVariant.price || 0)) * oilQuantity;
      const preparationTotal = selection
        ? selection.expectedTotal
        : (meta && gramsPerUnit ? meta.unitPrice * Math.round(gramsPerUnit / 100) * oilQuantity : 0);
      return {
        concentration: concentration,
        meta: meta || null,
        service: selection ? selection.service : null,
        serviceQuantity: selection ? selection.serviceQuantity : 0,
        serviceMode: selection ? selection.mode : '',
        preparationUnits: selection ? selection.preparationUnits : 0,
        expectedTotal: preparationTotal,
        oilSubtotal: oilSubtotal,
        estimatedTotal: oilSubtotal + preparationTotal,
        actualTotal: selection ? selection.actualTotal : 0,
        priceMatches: !!(selection && selection.priceMatches),
        gramsPerUnit: gramsPerUnit,
        sizeKey: sizeKeyFromGrams(gramsPerUnit),
        oilQuantity: oilQuantity,
        bottles: bottles,
        previewValid: previewValid,
        valid: !!(configured && previewValid && selection && selection.priceMatches)
      };
    }

    function updateChoiceCards() {
      const availableInputs = [];
      concentrationInputs.forEach(function (input) {
        const preview = snapshot(String(input.value));
        const usable = configured ? preview.valid : preview.previewValid;
        input.disabled = !usable;
        const choice = input.closest('[data-finished-choice]');
        if (!choice) return;
        choice.classList.toggle('is-unavailable', !usable);
        choice.classList.toggle('has-price-mismatch', !!(preview.service && !preview.priceMatches));
        choice.setAttribute('aria-disabled', String(!usable));
        if (usable) availableInputs.push(input);

        const yieldLabel = choice.querySelector('[data-finished-choice-yield]');
        if (yieldLabel) yieldLabel.textContent = preview.bottles ? preview.bottles + ' bottles' : 'Select size';
        const priceLabel = choice.querySelector('[data-finished-choice-price]');
        if (priceLabel) priceLabel.textContent = preview.expectedTotal ? formatCompactPreparationMoney(preview.expectedTotal) : '—';
        const image = choice.querySelector('[data-finished-choice-image]');
        if (image && preview.sizeKey) {
          const source = image.getAttribute('data-image-' + preview.sizeKey);
          if (source && image.getAttribute('src') !== source) image.setAttribute('src', source);
          const presentation = preview.sizeKey === '5kg'
            ? 'organized cases representing '
            : preview.sizeKey === '1kg'
              ? 'organized trays representing '
              : '';
          image.alt = 'Packaging preview with ' + presentation + preview.bottles + ' finished 100 ml spray bottles';
        }
      });

      const checked = concentrationInputs.find(function (input) { return input.checked && !input.disabled; });
      toggle.disabled = !availableInputs.length;
      if (!checked && toggle.checked) toggle.checked = false;
    }

    function updateContactLink(data) {
      if (!contactLink) return;
      const base = root.getAttribute('data-contact-base-url') || contactLink.getAttribute('href') || '';
      if (!base) return;
      try {
        const url = new URL(base, window.location.origin);
        const productTitle = root.getAttribute('data-product-title') || document.title;
        url.searchParams.set('subject', 'Custom glass bottle catalogue');
        url.searchParams.set('product', productTitle);
        if (currentVariant && currentVariant.title && currentVariant.title !== 'Default Title') {
          url.searchParams.set('variant', String(currentVariant.title));
        }
        if (data && data.meta) url.searchParams.set('concentration', data.meta.label);
        if (data && data.bottles) url.searchParams.set('yield', data.bottles + ' × 100 ml bottles');
        contactLink.href = url.pathname + url.search;
      } catch (error) {}
    }

    function emitChange(active, data) {
      const requested = !!toggle.checked;
      const nextState = [requested, active, data.concentration, data.bottles, data.expectedTotal, currentVariant && currentVariant.id, data.oilQuantity].join('|');
      if (nextState === lastEventState) return;
      lastEventState = nextState;
      try {
        form.dispatchEvent(new CustomEvent('pellair:finished-perfume-change', {
          bubbles: true,
          detail: {
            requested: requested,
            active: active,
            concentration: data.meta ? data.meta.label : '',
            bottles: data.bottles,
            summary: active && data.meta ? data.meta.label + ' · ' + data.bottles + ' × 100 ml' : ''
          }
        }));
      } catch (error) {}
    }

    function render() {
      updateChoiceCards();
      const selected = snapshot();
      const requested = !!toggle.checked;
      const active = requested && selected.valid;
      const previewActive = requested && !configured && selected.previewValid;
      optionsFieldset.disabled = false;
      form.classList.toggle('has-finished-perfume', active || previewActive);
      root.classList.toggle('is-selected', active);
      root.classList.toggle('is-preview-selected', previewActive);
      syncSizeButtons(selected.sizeKey);
      if (result) result.hidden = !requested;
      if (removeButton) removeButton.hidden = !requested;

      if (summaryTarget) {
        const previews = concentrationInputs.map(function (input) { return snapshot(String(input.value)); }).filter(function (preview) {
          return preview.previewValid && preview.expectedTotal > 0;
        });
        const lowestPreparation = previews.reduce(function (lowest, preview) {
          return !lowest || preview.expectedTotal < lowest ? preview.expectedTotal : lowest;
        }, 0);
        const defaultCopy = lowestPreparation
          ? 'From ' + formatCompactPreparationMoney(lowestPreparation)
          : (summaryTarget.getAttribute('data-default-copy') || 'Optional preparation');
        summaryTarget.textContent = active && selected.meta
          ? selected.meta.shortLabel + ' · ' + selected.bottles + ' bottles · + ' + formatCompactPreparationMoney(selected.expectedTotal)
          : defaultCopy;
      }
      if (stateTarget) stateTarget.textContent = active ? 'Selected' : 'Optional';
      if (quantityTarget) {
        quantityTarget.value = String(selected.oilQuantity);
        quantityTarget.min = quantityInput ? String(quantityInput.min || 1) : '1';
        quantityTarget.step = quantityInput ? String(quantityInput.step || 1) : '1';
        if (quantityInput && quantityInput.max !== '') quantityTarget.max = String(quantityInput.max);
        else quantityTarget.removeAttribute('max');
        quantityTarget.disabled = !currentVariant;
      }
      if (sizeLabelTarget) sizeLabelTarget.textContent = selected.sizeKey === '100g' ? '100 gm' : (selected.sizeKey || 'Selected size');
      if (quantityMinus && quantityInput) quantityMinus.disabled = selected.oilQuantity <= Number(quantityInput.min || 1);
      if (quantityPlus && quantityInput) {
        const quantityMaximum = quantityInput.max === '' ? null : Number(quantityInput.max);
        quantityPlus.disabled = quantityMaximum != null && Number.isFinite(quantityMaximum) && selected.oilQuantity >= quantityMaximum;
      }

      if (requested) {
        if (selected.valid || previewActive) {
          if (yieldTarget) yieldTarget.textContent = selected.bottles + ' × 100 ml';
          if (priceTarget) priceTarget.textContent = formatMoney(selected.expectedTotal, theme.currency);
          if (oilPriceTarget) oilPriceTarget.textContent = formatMoney(selected.oilSubtotal, theme.currency);
          if (totalPriceTarget) totalPriceTarget.textContent = formatMoney(selected.estimatedTotal, theme.currency);
          if (messageTarget) {
            messageTarget.hidden = !previewActive;
            messageTarget.textContent = previewActive
              ? 'Connect preparation pricing in Shopify before checkout.'
              : '';
            messageTarget.classList.toggle('is-error', previewActive);
          }
        } else {
          if (yieldTarget) yieldTarget.textContent = 'Choose an eligible size';
          if (priceTarget) priceTarget.textContent = '—';
          if (oilPriceTarget) oilPriceTarget.textContent = selected.oilSubtotal ? formatMoney(selected.oilSubtotal, theme.currency) : '—';
          if (totalPriceTarget) totalPriceTarget.textContent = '—';
          if (messageTarget) {
            messageTarget.hidden = false;
            messageTarget.textContent = selected.service && !selected.priceMatches
              ? 'Preparation pricing needs store setup before this option can be added.'
              : selected.gramsPerUnit
                ? 'This size and concentration are not configured.'
                : 'Select a 100 gm, 1 kg or 5 kg oil size.';
            messageTarget.classList.add('is-error');
          }
        }
      }

      updateContactLink(selected);
      emitChange(active, selected);
    }

    function cartItems(variant, sellingPlanId) {
      currentVariant = variant || currentVariant;
      const selected = snapshot();
      if (!toggle.checked || !selected.valid || !currentVariant) return [];
      const productTitle = root.getAttribute('data-product-title') || 'Selected fragrance oil';
      const variantTitle = currentVariant.title && currentVariant.title !== 'Default Title'
        ? String(currentVariant.title)
        : 'Standard oil';
      const batchReference = String(currentVariant.id) + '-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7);
      const serviceUnitsPerOil = Math.max(1, Math.round(selected.serviceQuantity / selected.oilQuantity));
      const oilItem = {
        id: Number(currentVariant.id),
        quantity: selected.oilQuantity,
        properties: {
          'Preparation': 'Finished perfume conversion',
          'Concentration': selected.meta.label,
          'Estimated yield': selected.bottles + ' × 100 ml bottles',
          'Packaging': 'Plastic spray bottles included',
          'Resting note': 'Freshly blended — allow 30–40 days',
          '_Finished perfume batch': batchReference,
          '_Finished perfume role': 'oil',
          '_Finished perfume units per oil': String(serviceUnitsPerOil)
        }
      };
      if (sellingPlanId) oilItem.selling_plan = Number(sellingPlanId);

      return [oilItem, {
        id: Number(selected.service.id),
        quantity: selected.serviceQuantity,
        properties: {
          'Prepared from': productTitle + ' — ' + variantTitle,
          'Concentration': selected.meta.label,
          'Estimated yield': selected.bottles + ' × 100 ml bottles',
          'Bottle format': '100 ml',
          'Packaging': 'Plastic spray bottles included',
          'Resting note': 'Freshly blended — allow 30–40 days',
          '_Preparation units': String(selected.preparationUnits),
          '_Oil variant': String(currentVariant.id),
          '_Finished perfume batch': batchReference,
          '_Finished perfume role': 'service',
          '_Finished perfume units per oil': String(serviceUnitsPerOil)
        }
      }];
    }

    scope.listen(toggle, 'change', render);
    scope.listen(root, 'change', function (event) {
      if (event.target.matches('input[name="finished_perfume_concentration"]')) {
        toggle.checked = true;
        render();
      }
    });

    if (removeButton) scope.listen(removeButton, 'click', function () {
      toggle.checked = false;
      concentrationInputs.forEach(function (input) { input.checked = false; });
      render();
      const firstAvailable = concentrationInputs.find(function (input) { return !input.disabled; });
      if (firstAvailable) firstAvailable.focus();
    });

    sizeButtons.forEach(function (button) {
      scope.listen(button, 'click', function () {
        const sizeKey = button.getAttribute('data-finished-perfume-size') || '';
        const sourceInput = productSizeInput(sizeKey);
        if (!sourceInput || sourceInput.disabled) return;
        if (!sourceInput.checked) sourceInput.click();
        else render();
      });
    });

    function changeQuantity(direction) {
      if (!quantityInput) return;
      const minimum = Number(quantityInput.min || 1) || 1;
      const step = Number(quantityInput.step || 1) || 1;
      const maximum = quantityInput.max === '' ? Infinity : Number(quantityInput.max);
      const current = Number.isFinite(quantityInput.valueAsNumber) ? quantityInput.valueAsNumber : minimum;
      const next = Math.min(maximum, Math.max(minimum, current + direction * step));
      if (next === current) return;
      quantityInput.value = String(next);
      quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (quantityMinus) scope.listen(quantityMinus, 'click', function () { changeQuantity(-1); });
    if (quantityPlus) scope.listen(quantityPlus, 'click', function () { changeQuantity(1); });
    if (quantityTarget) scope.listen(quantityTarget, 'change', function () {
      if (!quantityInput) return;
      const minimum = Number(quantityInput.min || 1) || 1;
      const step = Number(quantityInput.step || 1) || 1;
      const maximum = quantityInput.max === '' ? Infinity : Number(quantityInput.max);
      const requested = Number(quantityTarget.value);
      const aligned = minimum + Math.max(0, Math.round((requested - minimum) / step)) * step;
      quantityInput.value = String(Math.min(maximum, Math.max(minimum, Number.isFinite(aligned) ? aligned : minimum)));
      quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    });

    return {
      isActive: function () { return !!(toggle.checked && snapshot().valid); },
      isRequested: function () { return !!toggle.checked; },
      needsSetup: function () { return !!(toggle.checked && !configured); },
      sync: function (variant, oilUnitPrice) {
        currentVariant = variant || null;
        currentOilUnitPrice = Math.max(0, Number(oilUnitPrice != null ? oilUnitPrice : variant && variant.price || 0));
        render();
      },
      items: cartItems,
      destroy: function () {
        form.classList.remove('has-finished-perfume');
        scope.destroy();
      }
    };
  }

  function createProductFormController(form) {
    const scope = createScope();
    const productRoot = form.closest('[data-product]') || form;
    const optionRoot = form.querySelector('[data-option-groups]');
    const groups = optionRoot
      ? Array.from(optionRoot.querySelectorAll('[data-option-index]')).sort(function (a, b) {
          return Number(a.getAttribute('data-option-index')) - Number(b.getAttribute('data-option-index'));
        })
      : [];
    const variantInput = form.querySelector('[data-variant-id-input]') || form.querySelector('[name="id"]');
    const dataElement = form.querySelector('[data-variant-data]');
    const quantityRuleDataElement = form.querySelector('[data-quantity-rule-data]');
    const sellingPlanSelect = form.querySelector('[data-selling-plan]');
    const sellingPlanDataElement = form.querySelector('[data-selling-plan-data]');
    const price = productRoot.querySelector('[data-product-price]');
    const selectedTitles = Array.from(form.querySelectorAll('[data-selected-variant-title]'));
    const selectedPrice = form.querySelector('[data-selected-variant-price]');
    const addButton = form.querySelector('[data-add-to-cart]') || form.querySelector('.product-submit-button') || form.querySelector('button[type="submit"][name="add"]') || form.querySelector('button[type="submit"]');
    const oldBuyNow = form.querySelector('[data-buy-now]');
    const dynamicCheckout = form.querySelector('.product-form__dynamic-checkout');
    const status = form.querySelector('[data-product-status]');
    const quantity = form.querySelector('input[name="quantity"]');
    const productUrl = productRoot.getAttribute('data-product-url') || window.location.pathname;
    let addController = null;
    let variants = [];
    let quantityRules = {};
    let sellingPlanData = { requiresSellingPlan: false, variants: {} };
    let sellingPlanDataReady = false;
    let activeAllocation = null;
    let planSelectionValid = true;
    let currentVariant = null;
    let pending = false;
    let finishedPerfume = null;
    const initialPlanMarkup = sellingPlanSelect ? sellingPlanSelect.innerHTML : '';
    const initialPlanValue = sellingPlanSelect ? sellingPlanSelect.value : '';
    const initialPlanSelectDisabled = sellingPlanSelect ? sellingPlanSelect.disabled : false;

    if (dataElement) {
      try { variants = JSON.parse(dataElement.textContent.trim() || '[]'); } catch (error) { variants = []; }
    }

    if (quantityRuleDataElement) {
      try {
        const parsedQuantityRules = JSON.parse(quantityRuleDataElement.textContent.trim() || '{}');
        quantityRules = parsedQuantityRules && parsedQuantityRules.variants && typeof parsedQuantityRules.variants === 'object'
          ? parsedQuantityRules.variants
          : {};
      } catch (error) {
        quantityRules = {};
      }
    }

    if (sellingPlanDataElement) {
      try {
        const parsed = JSON.parse(sellingPlanDataElement.textContent.trim() || '{}');
        if (parsed && parsed.variants && typeof parsed.variants === 'object') {
          sellingPlanData = {
            requiresSellingPlan: !!parsed.requiresSellingPlan,
            variants: parsed.variants
          };
          sellingPlanDataReady = true;
        }
      } catch (error) {}
    }

    /* Malformed or missing variant data must leave Shopify's native form untouched. */
    if (!variants.length || !variantInput || (sellingPlanSelect && !sellingPlanDataReady)) {
      return { destroy: scope.destroy };
    }

    finishedPerfume = createFinishedPerfumeConfigurator(form);

    function valuesForVariant(variant) {
      if (!variant) return [];
      if (Array.isArray(variant.options)) return variant.options.map(String);
      return [variant.option1, variant.option2, variant.option3].filter(function (value) { return value != null; }).map(String);
    }

    function selectedValues() {
      return groups.map(function (group) {
        const checked = group.querySelector('[data-option-value]:checked');
        return checked ? String(checked.value) : '';
      });
    }

    function variantById(id) {
      return variants.find(function (variant) { return String(variant.id) === String(id); }) || null;
    }

    function exactVariant(values) {
      return variants.find(function (variant) {
        const options = valuesForVariant(variant);
        return values.every(function (value, index) { return String(options[index]) === String(value); });
      }) || null;
    }

    function variantTitle(variant) {
      if (!variant) return 'Unavailable combination';
      if (variant.title && variant.title !== 'Default Title') return String(variant.title).replace(/\s*\/\s*/g, ' / ');
      const values = valuesForVariant(variant).filter(Boolean);
      return values.length ? values.join(' / ') : (strings.standardOil || 'Standard oil');
    }

    function announce(message, error) {
      if (!status) return;
      status.textContent = message || '';
      status.hidden = !message;
      status.setAttribute('role', error ? 'alert' : 'status');
      status.setAttribute('aria-live', error ? 'assertive' : 'polite');
      status.classList.toggle('is-error', !!error);
      status.dataset.state = error ? 'error' : (message ? 'status' : 'idle');
    }

    function notifyAddResult(message, state) {
      try {
        form.dispatchEvent(new CustomEvent('pellair:product-add-result', {
          bubbles: true,
          detail: {
            message: message || '',
            state: state || 'status'
          }
        }));
      } catch (error) {}
    }

    function sellingPlanEntry(variant) {
      if (!sellingPlanDataReady || !variant) return null;
      return sellingPlanData.variants[String(variant.id)] || null;
    }

    function allocationsForVariant(variant) {
      const entry = sellingPlanEntry(variant);
      const allocations = Array.isArray(entry) ? entry : (entry && entry.allocations);
      return Array.isArray(allocations) ? allocations.filter(function (allocation) {
        return allocation && allocation.planId != null && allocation.price != null;
      }) : [];
    }

    function variantRequiresSellingPlan(variant) {
      const entry = sellingPlanEntry(variant);
      return entry && typeof entry.requiresSellingPlan === 'boolean'
        ? entry.requiresSellingPlan
        : sellingPlanData.requiresSellingPlan;
    }

    function cleanPlanText(value, fallback) {
      const cleaned = String(value == null ? '' : value)
        .replace(/<[^>]*>/g, ' ')
        .replace(/[\u0000-\u001f\u007f]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 120);
      return cleaned || fallback || '';
    }

    function syncSellingPlans(variant) {
      activeAllocation = null;
      planSelectionValid = true;
      if (!sellingPlanSelect || !sellingPlanDataReady) return;

      const allocations = allocationsForVariant(variant);
      const requiresSellingPlan = variantRequiresSellingPlan(variant);
      const availableIds = new Set(allocations.map(function (allocation) { return String(allocation.planId); }));
      const previousId = String(sellingPlanSelect.value || '');
      const fragment = document.createDocumentFragment();

      if (!requiresSellingPlan) {
        const oneTime = document.createElement('option');
        oneTime.value = '';
        oneTime.textContent = 'One-time purchase';
        fragment.appendChild(oneTime);
      }

      const groupElements = new Map();
      allocations.forEach(function (allocation) {
        const groupName = cleanPlanText(allocation.groupName, '');
        let parent = fragment;
        if (groupName) {
          if (!groupElements.has(groupName)) {
            const groupElement = document.createElement('optgroup');
            groupElement.label = groupName;
            groupElements.set(groupName, groupElement);
            fragment.appendChild(groupElement);
          }
          parent = groupElements.get(groupName);
        }
        const option = document.createElement('option');
        option.value = String(allocation.planId);
        option.textContent = cleanPlanText(allocation.planName, 'Purchase option');
        parent.appendChild(option);
      });

      if (requiresSellingPlan && !allocations.length) {
        const unavailable = document.createElement('option');
        unavailable.value = '';
        unavailable.textContent = 'No purchase option available';
        unavailable.disabled = true;
        unavailable.selected = true;
        fragment.appendChild(unavailable);
      }

      sellingPlanSelect.replaceChildren(fragment);
      sellingPlanSelect.disabled = requiresSellingPlan && !allocations.length;

      let selectedId = '';
      if (previousId && availableIds.has(previousId)) {
        sellingPlanSelect.value = previousId;
        selectedId = String(sellingPlanSelect.value || '');
      } else if (requiresSellingPlan && allocations.length) {
        sellingPlanSelect.value = String(allocations[0].planId);
        selectedId = String(sellingPlanSelect.value || '');
      } else {
        sellingPlanSelect.value = '';
      }

      activeAllocation = allocations.find(function (allocation) {
        return String(allocation.planId) === selectedId;
      }) || null;
      planSelectionValid = !requiresSellingPlan || !!activeAllocation;
    }

    function measurementValue(measurement, camelName, snakeName, fallback) {
      if (!measurement) return fallback;
      if (measurement[camelName] != null) return measurement[camelName];
      if (measurement[snakeName] != null) return measurement[snakeName];
      return fallback;
    }

    function updatePrice(variant, allocation) {
      if (!price || !variant || variant.price == null) return;
      const compare = Number(allocation ? allocation.compareAtPrice || 0 : variant.compare_at_price || 0);
      const current = Number(allocation ? allocation.price || 0 : variant.price || 0);
      const currentTarget = price.querySelector('[data-price-current]');
      const compareTarget = price.querySelector('[data-price-compare]');
      const regularLabel = price.querySelector('[data-price-regular-label]');
      const currentLabel = price.querySelector('[data-price-current-label]');
      const unitTarget = price.querySelector('[data-unit-price]');
      if (selectedPrice) {
        selectedPrice.textContent = formatMoney(current, theme.currency);
        selectedPrice.classList.remove('is-updated');
        if (!reduceMotion.matches && typeof selectedPrice.animate === 'function') {
          selectedPrice.animate([
            { opacity: 0.58, transform: 'translateY(3px)' },
            { opacity: 1, transform: 'translateY(0)' }
          ], { duration: 210, easing: 'cubic-bezier(.22,.61,.36,1)' });
        }
      }
      if (currentTarget) {
        currentTarget.textContent = formatMoney(current, theme.currency);
        const onSale = compare > current;
        if (regularLabel) {
          regularLabel.textContent = 'Regular price';
          regularLabel.hidden = !onSale;
        }
        if (currentLabel) {
          currentLabel.textContent = onSale ? 'Sale price' : 'Price';
          currentLabel.hidden = false;
        }
        if (compareTarget) {
          compareTarget.textContent = onSale ? formatMoney(compare, theme.currency) : '';
          compareTarget.hidden = !onSale;
        }
        if (unitTarget) {
          const measurement = allocation ? allocation.unitPriceMeasurement : variant.unit_price_measurement;
          const unitPrice = Number(allocation ? allocation.unitPrice : variant.unit_price || 0);
          const unitCurrent = unitTarget.querySelector('[data-unit-price-current]');
          const unitReference = unitTarget.querySelector('[data-unit-price-reference]');
          if (measurement && unitPrice > 0) {
            if (unitCurrent) unitCurrent.textContent = formatMoney(unitPrice, theme.currency);
            if (unitReference) {
              const referenceValue = measurementValue(measurement, 'referenceValue', 'reference_value', 1);
              const referenceUnit = measurementValue(measurement, 'referenceUnit', 'reference_unit', '');
              unitReference.textContent = (Number(referenceValue || 1) === 1 ? '' : referenceValue) + String(referenceUnit || '');
            }
            unitTarget.hidden = false;
          } else {
            if (unitCurrent) unitCurrent.textContent = '';
            if (unitReference) unitReference.textContent = '';
            unitTarget.hidden = true;
          }
        }
      } else {
        price.innerHTML = '<div class="price">' +
          (compare > current ? '<s>' + escapeHTML(formatMoney(compare, theme.currency)) + '</s>' : '') +
          '<span>' + escapeHTML(formatMoney(current, theme.currency)) + '</span></div>';
      }
    }

    function updateMedia(variant, userInitiated) {
      const mediaId = variant && variant.featured_media && variant.featured_media.id
        ? String(variant.featured_media.id)
        : String(variant && (variant.featured_media_id || variant.featured_image && variant.featured_image.id) || '');
      if (!mediaId) return;
      const mediaItems = Array.from(productRoot.querySelectorAll('[data-product-media-id]'));
      let active = null;
      mediaItems.forEach(function (item) {
        const selected = String(item.getAttribute('data-product-media-id')) === mediaId;
        item.classList.toggle('is-active', selected);
        item.classList.toggle('is-variant-media', selected);
        if (selected) item.setAttribute('data-active-media', '');
        else item.removeAttribute('data-active-media');
        if (selected) active = item;
      });
      productRoot.setAttribute('data-selected-media-id', mediaId);
      if (userInitiated && active && active.parentElement && active.parentElement.scrollWidth > active.parentElement.clientWidth) {
        active.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
      }
    }

    function updateAvailability() {
      const selected = selectedValues();
      groups.forEach(function (group) {
        const index = Number(group.getAttribute('data-option-index') || 0);
        group.querySelectorAll('[data-option-value]').forEach(function (input) {
          const inputValue = String(input.value);
          const candidates = variants.filter(function (variant) {
            const values = valuesForVariant(variant);
            if (String(values[index]) !== inputValue) return false;
            for (let priorIndex = 0; priorIndex < index; priorIndex += 1) {
              if (selected[priorIndex] && String(values[priorIndex]) !== String(selected[priorIndex])) return false;
            }
            return true;
          });
          const exists = candidates.length > 0;
          const available = candidates.some(function (variant) { return !!variant.available; });
          input.disabled = !available;
          input.setAttribute('aria-label', String(input.value) + (exists && !available
            ? ', sold out for this selection'
            : (!exists ? ', unavailable for this selection' : '')));
          const label = input.closest('.option-pill') || input.closest('label');
          if (label) {
            label.classList.toggle('is-unavailable', !exists);
            label.classList.toggle('is-sold-out', exists && !available);
            label.classList.toggle('is-selected', !!input.checked);
            label.setAttribute('aria-disabled', String(!available));
          }
        });
      });
    }

    function setButtonLabel(button, label) {
      if (!button) return;
      const target = button.querySelector('[data-add-to-cart-text], [data-button-label]');
      if (target) target.textContent = label;
      else button.textContent = label;
    }

    function updateButtons(variant) {
      const configuredRequested = !!(finishedPerfume && finishedPerfume.isRequested());
      const configuredOrder = !!(finishedPerfume && finishedPerfume.isActive());
      const preparationNeedsSetup = !!(finishedPerfume && finishedPerfume.needsSetup && finishedPerfume.needsSetup());
      const configurationValid = !configuredRequested || configuredOrder;
      const available = !!(variant && variant.available) && planSelectionValid && configurationValid && !pending;
      if (addButton) {
        addButton.disabled = !available;
        setButtonLabel(addButton, pending
          ? 'Adding…'
          : (!variant ? 'Unavailable' : (!variant.available ? 'Sold out' : (!planSelectionValid ? 'Unavailable' : (preparationNeedsSetup ? 'Preparation setup required' : (!configurationValid ? 'Choose eligible size' : (configuredOrder ? 'Add configured order' : 'Add to cart')))))));
      }
      if (oldBuyNow) {
        oldBuyNow.disabled = true;
        oldBuyNow.hidden = true;
      }
      form.classList.toggle('is-purchase-unavailable', !available);
      if (dynamicCheckout) {
        const checkoutHidden = !available || configuredOrder;
        dynamicCheckout.hidden = checkoutHidden;
        dynamicCheckout.setAttribute('aria-hidden', String(checkoutHidden));
        if ('inert' in dynamicCheckout) dynamicCheckout.inert = checkoutHidden;
      }
    }

    function quantityRuleForVariant(variant) {
      const explicit = variant && quantityRules[String(variant.id)];
      const embedded = variant && variant.quantity_rule;
      const rule = explicit || embedded || {};
      const minimum = Math.max(1, Number(rule.min || 1) || 1);
      const increment = Math.max(1, Number(rule.increment || 1) || 1);
      const maximumValue = rule.max == null || rule.max === '' ? null : Number(rule.max);
      const maximum = Number.isFinite(maximumValue) && maximumValue >= minimum ? maximumValue : null;
      return { min: minimum, increment: increment, max: maximum };
    }

    function syncQuantityButtons() {
      if (!quantity) return;
      const current = Number.isFinite(quantity.valueAsNumber) ? quantity.valueAsNumber : Number(quantity.min || 1);
      const minimum = Number(quantity.min || 1);
      const maximum = quantity.max === '' ? null : Number(quantity.max);
      const minus = form.querySelector('[data-qty-minus]');
      const plus = form.querySelector('[data-qty-plus]');
      if (minus) minus.disabled = current <= minimum;
      if (plus) plus.disabled = maximum != null && Number.isFinite(maximum) && current >= maximum;
    }

    function updateQuantityRule(variant) {
      if (!quantity || !variant) return;
      const rule = quantityRuleForVariant(variant);
      quantity.min = String(rule.min);
      quantity.step = String(rule.increment);
      if (rule.max == null) quantity.removeAttribute('max');
      else quantity.max = String(rule.max);
      const current = Number.isFinite(quantity.valueAsNumber) ? quantity.valueAsNumber : rule.min;
      let aligned = rule.min + Math.max(0, Math.ceil((current - rule.min) / rule.increment)) * rule.increment;
      if (rule.max != null && aligned > rule.max) {
        aligned = rule.min + Math.max(0, Math.floor((rule.max - rule.min) / rule.increment)) * rule.increment;
      }
      quantity.value = String(Math.max(rule.min, aligned));
      syncQuantityButtons();
    }

    function syncRadios(variant) {
      const values = valuesForVariant(variant);
      groups.forEach(function (group, index) {
        group.querySelectorAll('[data-option-value]').forEach(function (input) {
          input.checked = String(input.value) === String(values[index]);
        });
      });
    }

    function updateURL(variant) {
      if (!variant || !window.history || !window.history.replaceState) return;
      try {
        const url = new URL(productUrl, window.location.href);
        const current = new URL(window.location.href);
        current.searchParams.forEach(function (value, key) {
          if (key !== 'variant' && key !== 'option_values' && !url.searchParams.has(key)) url.searchParams.append(key, value);
        });
        url.searchParams.delete('option_values');
        url.searchParams.set('variant', variant.id);
        if (sellingPlanSelect) {
          if (sellingPlanSelect.value) url.searchParams.set('selling_plan', sellingPlanSelect.value);
          else url.searchParams.delete('selling_plan');
        }
        url.hash = current.hash;
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      } catch (error) {}
    }

    function applyVariant(variant, options) {
      const settings = options || {};
      currentVariant = variant;
      if (variant && variantInput) variantInput.value = String(variant.id);
      if (variant && settings.syncRadios !== false) syncRadios(variant);
      const title = variantTitle(variant);
      selectedTitles.forEach(function (element) { element.textContent = title; });
      syncSellingPlans(variant);
      updatePrice(variant, activeAllocation);
      updateQuantityRule(variant);
      if (finishedPerfume) finishedPerfume.sync(variant, activeAllocation ? activeAllocation.price : variant && variant.price);
      updateButtons(variant);
      updateAvailability();
      updateMedia(variant, !!settings.userInitiated);
      if (settings.updateURL) updateURL(variant);
      if (!variant) announce('That option combination is unavailable.', true);
      else if (!variant.available) announce(title + ' is sold out.', true);
      else if (!planSelectionValid) announce('No purchase option is available for ' + title + '.', true);
      else if (settings.userInitiated) announce(title + ' selected.');
      try {
        form.dispatchEvent(new CustomEvent('pellair:variant-change', {
          bubbles: true,
          detail: { variant: variant }
        }));
      } catch (error) {}
    }

    function onOptionChange(event) {
      const input = event.target.closest('[data-option-value]');
      if (!input || input.disabled) return;
      const values = selectedValues();
      const variant = exactVariant(values);
      applyVariant(variant, {
        syncRadios: false,
        userInitiated: true,
        updateURL: true
      });
    }

    if (optionRoot) scope.listen(optionRoot, 'change', onOptionChange);

    if (finishedPerfume) {
      scope.listen(form, 'pellair:finished-perfume-change', function () {
        updateButtons(currentVariant);
      });
    }

    if (variantInput && variantInput.tagName === 'SELECT') {
      scope.listen(variantInput, 'change', function () {
        const variant = variantById(variantInput.value);
        applyVariant(variant, { syncRadios: true, userInitiated: true, updateURL: true });
      });
    }

    if (sellingPlanSelect && sellingPlanDataReady) {
      scope.listen(sellingPlanSelect, 'change', function () {
        syncSellingPlans(currentVariant);
        updatePrice(currentVariant, activeAllocation);
        if (finishedPerfume) finishedPerfume.sync(currentVariant, activeAllocation ? activeAllocation.price : currentVariant && currentVariant.price);
        updateButtons(currentVariant);
        if (currentVariant) updateURL(currentVariant);
        if (!planSelectionValid) {
          announce('That purchase option is unavailable for this variant.', true);
          return;
        }
        if (currentVariant && currentVariant.available) {
          const amount = Number(activeAllocation ? activeAllocation.price : currentVariant.price || 0);
          announce((activeAllocation ? 'Purchase option' : 'One-time purchase') + ' selected. Price updated to ' + formatMoney(amount, theme.currency) + '.');
        }
      });
    }

    scope.listen(form, 'click', function (event) {
      const decrement = event.target.closest('[data-qty-minus]');
      const increment = event.target.closest('[data-qty-plus]');
      if ((!decrement && !increment) || !quantity) return;
      event.preventDefault();
      const step = Number(quantity.step || 1) || 1;
      const min = quantity.min === '' ? 1 : Number(quantity.min);
      const max = quantity.max === '' ? Infinity : Number(quantity.max);
      const current = Number.isFinite(quantity.valueAsNumber) ? quantity.valueAsNumber : min;
      quantity.value = String(Math.min(max, Math.max(min, current + (increment ? step : -step))));
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
    });

    if (quantity) {
      scope.listen(quantity, 'input', function () {
        syncQuantityButtons();
        if (finishedPerfume) finishedPerfume.sync(currentVariant, activeAllocation ? activeAllocation.price : currentVariant && currentVariant.price);
      });
      scope.listen(quantity, 'change', function () {
        updateQuantityRule(currentVariant);
        if (finishedPerfume) finishedPerfume.sync(currentVariant, activeAllocation ? activeAllocation.price : currentVariant && currentVariant.price);
        updateButtons(currentVariant);
      });
    }

    scope.listen(form, 'submit', async function (event) {
      const submitter = event.submitter;
      if (!addButton || submitter !== addButton) return;
      if (!theme.cart || typeof theme.cart.add !== 'function') return;
      event.preventDefault();
      if (pending) return;
      if (!currentVariant || !currentVariant.available) {
        const unavailableMessage = 'Choose an available oil before adding it to your cart.';
        announce(unavailableMessage, true);
        notifyAddResult(unavailableMessage, 'error');
        return;
      }
      if (!planSelectionValid) {
        const planMessage = 'Choose an available purchase option before adding it to your cart.';
        announce(planMessage, true);
        notifyAddResult(planMessage, 'error');
        return;
      }
      const configuredRequested = !!(finishedPerfume && finishedPerfume.isRequested());
      const configuredOrder = !!(finishedPerfume && finishedPerfume.isActive());
      const preparationNeedsSetup = !!(finishedPerfume && finishedPerfume.needsSetup && finishedPerfume.needsSetup());
      if (configuredRequested && !configuredOrder) {
        const eligibilityMessage = preparationNeedsSetup
          ? 'Preparation pricing must be connected in Shopify before this add-on can be checked out.'
          : 'Choose a 100 gm, 1 kg or 5 kg oil size and an available concentration for preparation.';
        announce(eligibilityMessage, true);
        notifyAddResult(eligibilityMessage, 'error');
        return;
      }
      const configuredItems = configuredOrder
        ? finishedPerfume.items(currentVariant, sellingPlanSelect && sellingPlanSelect.value)
        : [];
      if (configuredOrder && (typeof theme.cart.addItems !== 'function' || configuredItems.length !== 2)) {
        const configurationMessage = 'The finished-perfume service could not be prepared. Turn it off or contact us for help.';
        announce(configurationMessage, true);
        notifyAddResult(configurationMessage, 'error');
        return;
      }
      pending = true;
      const submissionController = new AbortController();
      addController = submissionController;
      form.setAttribute('aria-busy', 'true');
      updateButtons(currentVariant);
      const addingMessage = configuredOrder
        ? 'Adding your oil and finished-perfume preparation to the cart.'
        : (strings.addingToCart || ('Adding ' + variantTitle(currentVariant) + ' to your cart.'));
      announce(addingMessage);
      notifyAddResult(addingMessage, 'loading');
      try {
        const outcome = configuredOrder
          ? await theme.cart.addItems(configuredItems, addButton, submissionController.signal)
          : await theme.cart.add(new FormData(form), addButton, submissionController.signal);
        if (outcome && outcome.added && outcome.refreshed === false) {
          const warningMessage = outcome.message || strings.addRefreshWarning || 'Item added, but the cart could not refresh. Open your cart to confirm.';
          announce(warningMessage, true);
          notifyAddResult(warningMessage, 'warning');
        } else {
          const successMessage = configuredOrder
            ? 'Configured oil and preparation added to your cart.'
            : (strings.addedToCart || 'Added to your cart.');
          announce(successMessage);
          notifyAddResult(successMessage, 'success');
        }
      } catch (error) {
        if (!isAbortError(error)) {
          const errorMessage = customerErrorMessage(error, strings.addFailed || 'This oil could not be added. Try again.');
          announce(errorMessage, true);
          notifyAddResult(errorMessage, 'error');
        }
      } finally {
        if (addController === submissionController) addController = null;
        pending = false;
        form.setAttribute('aria-busy', 'false');
        updateButtons(currentVariant);
      }
    });

    const initialId = variantInput ? variantInput.value : '';
    const initial = variantById(initialId) || exactVariant(selectedValues()) || variants[0] || null;
    try {
      applyVariant(initial, { syncRadios: !!initial, userInitiated: false, updateURL: false });
      form.classList.add('is-enhanced');
    } catch (error) {
      scope.destroy();
      throw error;
    }

    return {
      pageHide: function () {
        if (addController) addController.abort();
        addController = null;
        pending = false;
        form.setAttribute('aria-busy', 'false');
        updateButtons(currentVariant);
      },
      pageShow: function () {
        pending = false;
        form.setAttribute('aria-busy', 'false');
        if (finishedPerfume) finishedPerfume.sync(currentVariant, activeAllocation ? activeAllocation.price : currentVariant && currentVariant.price);
        updateButtons(currentVariant);
      },
      destroy: function () {
        if (addController) addController.abort();
        if (finishedPerfume) finishedPerfume.destroy();
        form.classList.remove('is-enhanced');
        if (sellingPlanSelect && sellingPlanSelect.isConnected) {
          sellingPlanSelect.innerHTML = initialPlanMarkup;
          sellingPlanSelect.value = initialPlanValue;
          sellingPlanSelect.disabled = initialPlanSelectDisabled;
        }
        scope.destroy();
      }
    };
  }

  function createMobileStickyPurchaseController(root) {
    const scope = createScope();
    const form = root.closest('[data-product-form]');
    if (!form) return { destroy: scope.destroy };

    const mainActions = form.querySelector('[data-main-purchase-actions]') || form.querySelector('[data-buy-actions]');
    const variantInput = form.querySelector('[data-variant-id-input]') || form.querySelector('[name="id"]');
    const mainAddButton = form.querySelector('[data-add-to-cart]');
    const dynamicCheckout = form.querySelector('[data-original-dynamic-checkout]') || form.querySelector('.product-form__dynamic-checkout');
    const priceRoot = (form.closest('[data-product]') || form).querySelector('[data-product-price]');
    const mainSelectedTitle = form.querySelector('.product-form__header [data-selected-variant-title]');
    const toggleButton = root.querySelector('[data-mobile-sticky-variant-toggle]');
    const menu = root.querySelector('[data-mobile-sticky-variant-menu]');
    const menuClose = root.querySelector('[data-mobile-sticky-menu-close]');
    const menuButtons = menu ? Array.from(menu.querySelectorAll('[data-mobile-sticky-variant-id]')) : [];
    const stickyTitle = root.querySelector('[data-mobile-sticky-variant-title]');
    const stickyPrice = root.querySelector('[data-mobile-sticky-price]');
    const stickyAdd = root.querySelector('[data-mobile-sticky-add]');
    const stickyAddLabel = root.querySelector('[data-mobile-sticky-add-label]');
    const stickyBuy = root.querySelector('[data-mobile-sticky-buy]');
    const stickyStatus = root.querySelector('[data-mobile-sticky-status]');
    const quantityInput = form.querySelector('input[name="quantity"]');
    const stickyQuantity = root.querySelector('[data-mobile-sticky-quantity]');
    const stickyFinishedPerfume = root.querySelector('[data-mobile-sticky-finished-perfume]');
    const headerSearch = document.querySelector('.header-search-details');
    const mobileQuery = window.matchMedia
      ? window.matchMedia('(max-width: 749px)')
      : { matches: window.innerWidth < 750 };
    let geometryFrame = 0;
    let addResetTimer = 0;
    let buyResetTimer = 0;
    let statusResetTimer = 0;
    let focusOutTimer = 0;
    let menuOpen = false;
    let addActivationPending = false;
    let buyActivationPending = false;
    let purchaseTemporarilySuppressed = false;

    if (!mainActions || !variantInput || !mainAddButton || !toggleButton || !menu || !stickyAdd || !stickyBuy) {
      root.hidden = true;
      root.setAttribute('aria-hidden', 'true');
      return { destroy: scope.destroy };
    }

    function announce(message, state, timeout) {
      if (!stickyStatus) return;
      window.clearTimeout(statusResetTimer);
      const copy = message || '';
      stickyStatus.textContent = copy;
      stickyStatus.hidden = !copy;
      stickyStatus.dataset.state = state || (copy ? 'status' : 'idle');
      if (copy && timeout) {
        statusResetTimer = window.setTimeout(function () {
          if (!stickyStatus.isConnected) return;
          stickyStatus.textContent = '';
          stickyStatus.hidden = true;
          stickyStatus.dataset.state = 'idle';
        }, timeout);
      }
    }

    function selectedMenuButton() {
      const selectedId = String(variantInput.value || '');
      return menuButtons.find(function (button) {
        return String(button.getAttribute('data-mobile-sticky-variant-id') || '') === selectedId;
      }) || null;
    }

    function enabledMenuButtons() {
      return menuButtons.filter(function (button) {
        return !button.disabled && button.getAttribute('aria-disabled') !== 'true';
      });
    }

    function syncRovingTabIndex(preferred) {
      const enabled = enabledMenuButtons();
      const selected = selectedMenuButton();
      const active = preferred && enabled.indexOf(preferred) !== -1
        ? preferred
        : (selected && enabled.indexOf(selected) !== -1 ? selected : enabled[0]);
      menuButtons.forEach(function (button) {
        button.tabIndex = button === active ? 0 : -1;
      });
      return active || null;
    }

    function setMenuOpen(next, options) {
      const settings = options || {};
      const open = !!next && menuButtons.length > 1 && !root.hidden && mobileQuery.matches;
      menuOpen = open;
      menu.hidden = !open;
      root.classList.toggle('is-menu-open', open);
      toggleButton.setAttribute('aria-expanded', String(open));
      if (open) {
        const focusTarget = syncRovingTabIndex(settings.preferred);
        if (settings.focus !== false && focusTarget) focusTarget.focus({ preventScroll: true });
      } else if (settings.restoreFocus && toggleButton.isConnected && !toggleButton.disabled) {
        toggleButton.focus({ preventScroll: true });
      }
    }

    function currentPriceText() {
      if (priceRoot) {
        const current = priceRoot.querySelector('[data-price-current]');
        if (current && current.textContent.trim()) return current.textContent.trim();
        const visiblePrice = priceRoot.querySelector('.price');
        if (visiblePrice && visiblePrice.textContent.trim()) {
          return visiblePrice.textContent.replace(/\s+/g, ' ').trim();
        }
      }
      const selected = selectedMenuButton();
      return selected ? String(selected.getAttribute('data-mobile-sticky-variant-price') || '') : '';
    }

    function originalBuyButton() {
      if (!dynamicCheckout) return null;
      return dynamicCheckout.querySelector('.shopify-payment-button__button, button[type="submit"], button');
    }

    function syncPurchaseState() {
      const selected = selectedMenuButton();
      const selectedAvailable = selected
        ? selected.getAttribute('data-mobile-sticky-variant-available') === 'true'
        : !mainAddButton.disabled;
      const formBusy = form.getAttribute('aria-busy') === 'true';
      const mainAddLabel = mainAddButton.querySelector('[data-add-to-cart-text], [data-button-label]');
      const addLabel = mainAddLabel
        ? mainAddLabel.textContent.trim()
        : (selectedAvailable ? 'Add to cart' : 'Unavailable');

      const nextAddDisabled = !selectedAvailable || mainAddButton.disabled || formBusy || addActivationPending;
      if (stickyAdd.disabled !== nextAddDisabled) stickyAdd.disabled = nextAddDisabled;
      const nextAddLabel = addLabel || 'Add to cart';
      if (stickyAddLabel && stickyAddLabel.textContent !== nextAddLabel) {
        stickyAddLabel.textContent = nextAddLabel;
      }

      const originalBuy = originalBuyButton();
      const dynamicUnavailable = !dynamicCheckout
        || dynamicCheckout.hidden
        || dynamicCheckout.getAttribute('aria-hidden') === 'true'
        || !originalBuy
        || originalBuy.disabled
        || originalBuy.getAttribute('aria-disabled') === 'true';
      const nextBuyDisabled = !selectedAvailable || formBusy || buyActivationPending || dynamicUnavailable;
      if (stickyBuy.disabled !== nextBuyDisabled) stickyBuy.disabled = nextBuyDisabled;
    }

    function syncQuantityState() {
      if (!stickyQuantity || !quantityInput) return;
      const quantity = Math.max(1, Number(quantityInput.value || quantityInput.min || 1) || 1);
      const nextQuantity = (strings.quantityShort || 'Qty') + ' ' + quantity;
      if (stickyQuantity.textContent !== nextQuantity) stickyQuantity.textContent = nextQuantity;
    }

    function syncFinishedPerfumeState(event) {
      if (!stickyFinishedPerfume) return;
      const detail = event && event.detail || {};
      const active = typeof detail.active === 'boolean'
        ? detail.active
        : form.classList.contains('has-finished-perfume');
      stickyFinishedPerfume.hidden = !active;
      if (active) stickyFinishedPerfume.setAttribute('aria-label', detail.summary || 'Finished perfume preparation selected');
      else stickyFinishedPerfume.removeAttribute('aria-label');
      syncPurchaseState();
      scheduleGeometry();
    }

    function syncVariantState() {
      const selectedId = String(variantInput.value || '');
      let selected = null;
      menuButtons.forEach(function (button) {
        const isSelected = String(button.getAttribute('data-mobile-sticky-variant-id') || '') === selectedId;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-selected', String(isSelected));
        if (isSelected) selected = button;
      });
      syncRovingTabIndex();

      if (stickyTitle) {
        const title = selected && selected.getAttribute('data-mobile-sticky-variant-title')
          || mainSelectedTitle && mainSelectedTitle.textContent
          || 'Selected oil';
        const nextTitle = String(title).trim();
        if (stickyTitle.textContent !== nextTitle) stickyTitle.textContent = nextTitle;
      }
      if (stickyPrice) {
        const nextPrice = currentPriceText();
        if (stickyPrice.textContent !== nextPrice) stickyPrice.textContent = nextPrice;
      }
      syncQuantityState();
      syncPurchaseState();
    }

    function overlayIsOpen() {
      const body = document.body;
      if (html.classList.contains('intro-pending') || html.classList.contains('cart-drawer-is-open') || html.classList.contains('mobile-nav-open')) return true;
      if (body.classList.contains('intro-pending') || body.classList.contains('cart-drawer-is-open') || body.classList.contains('mobile-nav-open') || body.classList.contains('finder-select-open')) return true;
      return !!document.querySelector('.header-search-details[open], [data-cart-drawer].is-open, [aria-modal="true"][aria-hidden="false"]');
    }

    function setVisible(visible) {
      const next = !!visible && mobileQuery.matches;
      const hidden = !next;
      const wasHidden = root.hidden;
      if (!next && !wasHidden) {
        setMenuOpen(false, { focus: false, restoreFocus: false });
        if (root.contains(document.activeElement) && document.activeElement.blur) {
          document.activeElement.blur();
        }
      }
      if (root.hidden !== hidden) root.hidden = hidden;
      const ariaHidden = String(hidden);
      if (root.getAttribute('aria-hidden') !== ariaHidden) root.setAttribute('aria-hidden', ariaHidden);
      if ('inert' in root && root.inert !== hidden) root.inert = hidden;
      if (document.body) {
        const bodyHasStickyClass = document.body.classList.contains('has-mobile-sticky-purchase');
        if (bodyHasStickyClass !== next) document.body.classList.toggle('has-mobile-sticky-purchase', next);
      }
      if (next && wasHidden) window.requestAnimationFrame(syncMobilePurchaseSpace);
      if (!next && html.style.getPropertyValue('--mobile-purchase-bar-space')) {
        html.style.removeProperty('--mobile-purchase-bar-space');
      }
    }

    function syncMobilePurchaseSpace() {
      if (!mobileQuery.matches || root.hidden) {
        if (html.style.getPropertyValue('--mobile-purchase-bar-space')) {
          html.style.removeProperty('--mobile-purchase-bar-space');
        }
        return;
      }
      const height = Math.ceil(root.getBoundingClientRect().height);
      const nextSpace = height > 0 ? height + 16 + 'px' : '';
      if (nextSpace && html.style.getPropertyValue('--mobile-purchase-bar-space') !== nextSpace) {
        html.style.setProperty('--mobile-purchase-bar-space', nextSpace);
      }
    }

    function updateGeometry() {
      geometryFrame = 0;
      if (!mobileQuery.matches || purchaseTemporarilySuppressed) {
        setVisible(false);
        return;
      }

      const actionsRect = mainActions.getBoundingClientRect();
      const actionsHavePassed = actionsRect.bottom <= 0;
      const footerBuffer = Math.max(96, root.offsetHeight || 0);
      const footer = document.querySelector('[data-footer], .site-footer');
      const nearFooter = !!footer && footer.getBoundingClientRect().top <= window.innerHeight + footerBuffer;
      setVisible(actionsHavePassed && !nearFooter && !overlayIsOpen());
    }

    function scheduleGeometry() {
      if (!mobileQuery.matches) {
        if (geometryFrame) window.cancelAnimationFrame(geometryFrame);
        geometryFrame = 0;
        setVisible(false);
        return;
      }
      if (geometryFrame) return;
      geometryFrame = window.requestAnimationFrame(updateGeometry);
    }

    scope.listen(toggleButton, 'click', function () {
      setMenuOpen(!menuOpen, { focus: true });
    });

    if (menuClose) {
      scope.listen(menuClose, 'click', function () {
        setMenuOpen(false, { restoreFocus: true });
      });
    }

    scope.listen(toggleButton, 'keydown', function (event) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const enabled = enabledMenuButtons();
      if (!enabled.length) return;
      event.preventDefault();
      const preferred = event.key === 'ArrowUp' ? enabled[enabled.length - 1] : (selectedMenuButton() || enabled[0]);
      setMenuOpen(true, { focus: true, preferred: preferred });
    });

    scope.listen(menu, 'click', function (event) {
      const button = event.target.closest('[data-mobile-sticky-variant-id]');
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      const id = button.getAttribute('data-mobile-sticky-variant-id');
      if (!id) return;
      variantInput.value = id;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      syncVariantState();
      setMenuOpen(false, { restoreFocus: true });
    });

    scope.listen(menu, 'keydown', function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        setMenuOpen(false, { restoreFocus: true });
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Home' && event.key !== 'End') return;
      const enabled = enabledMenuButtons();
      if (!enabled.length) return;
      event.preventDefault();
      const current = event.target.closest('[data-mobile-sticky-variant-id]');
      const index = Math.max(0, enabled.indexOf(current));
      let target = enabled[0];
      if (event.key === 'End') target = enabled[enabled.length - 1];
      else if (event.key === 'ArrowDown') target = enabled[(index + 1) % enabled.length];
      else if (event.key === 'ArrowUp') target = enabled[(index - 1 + enabled.length) % enabled.length];
      syncRovingTabIndex(target);
      target.focus({ preventScroll: true });
    });

    scope.listen(root, 'focusout', function () {
      window.clearTimeout(focusOutTimer);
      focusOutTimer = window.setTimeout(function () {
        if (menuOpen && !root.contains(document.activeElement)) {
          setMenuOpen(false, { focus: false });
        }
      }, 0);
    });

    scope.listen(document, 'click', function (event) {
      if (menuOpen && !root.contains(event.target)) setMenuOpen(false, { focus: false });
    });

    scope.listen(document, 'keydown', function (event) {
      if (menuOpen && event.key === 'Escape') {
        event.preventDefault();
        setMenuOpen(false, { restoreFocus: true });
      }
    });

    scope.listen(stickyAdd, 'click', function (event) {
      event.preventDefault();
      if (stickyAdd.disabled || mainAddButton.disabled || form.getAttribute('aria-busy') === 'true' || addActivationPending) return;
      addActivationPending = true;
      setMenuOpen(false, { focus: false });
      syncPurchaseState();
      announce(strings.addingToCart || 'Adding the selected oil to your cart.', 'loading');
      try {
        if (form.requestSubmit) form.requestSubmit(mainAddButton);
        else mainAddButton.click();
      } catch (error) {
        mainAddButton.click();
      }
      window.clearTimeout(addResetTimer);
      addResetTimer = window.setTimeout(function () {
        if (form.getAttribute('aria-busy') !== 'true') {
          addActivationPending = false;
          syncPurchaseState();
        }
      }, 250);
    });

    scope.listen(stickyBuy, 'click', function (event) {
      event.preventDefault();
      const originalBuy = originalBuyButton();
      if (stickyBuy.disabled || buyActivationPending || !originalBuy || !dynamicCheckout || dynamicCheckout.hidden || dynamicCheckout.getAttribute('aria-hidden') === 'true' || originalBuy.disabled || originalBuy.getAttribute('aria-disabled') === 'true') return;
      buyActivationPending = true;
      purchaseTemporarilySuppressed = true;
      setMenuOpen(false, { focus: false });
      syncPurchaseState();
      scheduleGeometry();
      announce('Opening secure checkout.', 'loading');
      originalBuy.click();
      window.clearTimeout(buyResetTimer);
      buyResetTimer = window.setTimeout(function () {
        buyActivationPending = false;
        purchaseTemporarilySuppressed = false;
        syncPurchaseState();
        scheduleGeometry();
      }, 1800);
    });

    scope.listen(form, 'pellair:variant-change', syncVariantState);
    scope.listen(form, 'pellair:finished-perfume-change', syncFinishedPerfumeState);
    scope.listen(form, 'pellair:product-add-result', function (event) {
      const detail = event && event.detail || {};
      addActivationPending = detail.state === 'loading';
      syncPurchaseState();
      announce(detail.message || '', detail.state || 'status', detail.state === 'loading' ? 0 : 3800);
    });
    if (quantityInput) {
      scope.listen(quantityInput, 'input', syncQuantityState);
      scope.listen(quantityInput, 'change', syncQuantityState);
    }
    scope.listen(window, 'scroll', scheduleGeometry, { passive: true });
    scope.listen(window, 'resize', scheduleGeometry, { passive: true });
    scope.listen(window, 'orientationchange', scheduleGeometry, { passive: true });
    scope.listen(document, 'pellair:overlay:open', scheduleGeometry);
    if (headerSearch) scope.listen(headerSearch, 'toggle', scheduleGeometry);

    if (mobileQuery.addEventListener) {
      scope.listen(mobileQuery, 'change', scheduleGeometry);
    } else if (mobileQuery.addListener) {
      mobileQuery.addListener(scheduleGeometry);
      scope.add(function () { mobileQuery.removeListener(scheduleGeometry); });
    }

    if ('MutationObserver' in window) {
      let previousOverlayState = overlayIsOpen();
      const classObserver = new MutationObserver(function () {
        const nextOverlayState = overlayIsOpen();
        if (nextOverlayState === previousOverlayState) return;
        previousOverlayState = nextOverlayState;
        scheduleGeometry();
      });
      classObserver.observe(html, { attributes: true, attributeFilter: ['class'] });
      classObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      scope.add(function () { classObserver.disconnect(); });

      const formObserver = new MutationObserver(function () {
        if (form.getAttribute('aria-busy') !== 'true') addActivationPending = false;
        syncPurchaseState();
      });
      formObserver.observe(form, { attributes: true, attributeFilter: ['aria-busy'] });
      scope.add(function () { formObserver.disconnect(); });

      const addObserver = new MutationObserver(syncPurchaseState);
      addObserver.observe(mainAddButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
        childList: true,
        characterData: true,
        subtree: true
      });
      scope.add(function () { addObserver.disconnect(); });

      if (dynamicCheckout) {
        const checkoutObserver = new MutationObserver(function () {
          syncPurchaseState();
          scheduleGeometry();
        });
        checkoutObserver.observe(dynamicCheckout, {
          attributes: true,
          attributeFilter: ['disabled', 'hidden', 'aria-hidden', 'aria-disabled'],
          childList: true,
          subtree: true
        });
        scope.add(function () { checkoutObserver.disconnect(); });
      }

      if (priceRoot) {
        const priceObserver = new MutationObserver(function () {
          if (!stickyPrice) return;
          const nextPrice = currentPriceText();
          if (stickyPrice.textContent !== nextPrice) stickyPrice.textContent = nextPrice;
        });
        priceObserver.observe(priceRoot, { childList: true, characterData: true, subtree: true });
        scope.add(function () { priceObserver.disconnect(); });
      }
    }

    if ('ResizeObserver' in window) {
      const barResizeObserver = new ResizeObserver(syncMobilePurchaseSpace);
      barResizeObserver.observe(root);
      scope.add(function () { barResizeObserver.disconnect(); });
    }

    syncVariantState();
    syncQuantityState();
    syncFinishedPerfumeState();
    updateGeometry();

    return {
      pageHide: function () {
        setVisible(false);
        addActivationPending = false;
        buyActivationPending = false;
        purchaseTemporarilySuppressed = false;
      },
      pageShow: function () {
        addActivationPending = false;
        buyActivationPending = false;
        purchaseTemporarilySuppressed = false;
        syncVariantState();
        scheduleGeometry();
      },
      destroy: function () {
        window.clearTimeout(addResetTimer);
        window.clearTimeout(buyResetTimer);
        window.clearTimeout(statusResetTimer);
        window.clearTimeout(focusOutTimer);
        if (geometryFrame) window.cancelAnimationFrame(geometryFrame);
        setVisible(false);
        html.style.removeProperty('--mobile-purchase-bar-space');
        scope.destroy();
      }
    };
  }

  function createCartPageController(form) {
    const scope = createScope();
    const updateButton = form.querySelector('[data-cart-update]');
    const checkoutButton = form.querySelector('[data-cart-checkout], [name="checkout"]');
    const status = form.querySelector('[data-cart-page-status]');
    const autoUpdate = form.hasAttribute('data-cart-auto-update') || !!(updateButton && updateButton.classList.contains('visually-hidden'));
    let timer = 0;

    function announce(message, error) {
      if (!status) return;
      status.textContent = message || '';
      status.setAttribute('role', error ? 'alert' : 'status');
      status.setAttribute('aria-live', error ? 'assertive' : 'polite');
      status.classList.toggle('is-error', !!error);
      status.dataset.finishedPerfumePairMessage = message ? 'true' : 'false';
    }

    function batchRows(batch) {
      if (!batch) return [];
      return Array.from(form.querySelectorAll('[data-cart-line][data-finished-perfume-batch]')).filter(function (row) {
        return row.getAttribute('data-finished-perfume-batch') === batch;
      });
    }

    function rowRole(row) {
      return String(row && row.getAttribute('data-finished-perfume-role') || '').trim().toLowerCase();
    }

    function rowQuantity(row) {
      const input = row && row.querySelector('[data-cart-qty]');
      return Math.max(0, Math.round(Number(input && input.value || 0)));
    }

    function pairRatio(rows) {
      const explicit = rows.map(function (row) {
        return Number(row.getAttribute('data-finished-perfume-ratio') || 0);
      }).filter(function (value) { return Number.isInteger(value) && value > 0; });
      if (explicit.length) {
        const ratio = explicit[0];
        if (explicit.some(function (value) { return value !== ratio; })) return 0;
        return ratio;
      }

      const service = rows.find(function (row) { return rowRole(row) === 'service'; });
      const oil = rows.find(function (row) { return rowRole(row) === 'oil'; });
      const serviceVariantTitle = String(service && service.getAttribute('data-variant-title') || '');
      if (/(?:^|\s|\/)(?:100\s*(?:g|gm)|1\s*kg|5\s*kg)(?:\s|\/|$)/i.test(serviceVariantTitle)) return 1;
      const oilQuantity = rowQuantity(oil);
      const serviceQuantity = rowQuantity(service);
      const inferred = oilQuantity > 0 ? serviceQuantity / oilQuantity : 0;
      return Number.isInteger(inferred) && inferred > 0 ? inferred : 0;
    }

    function inspectPagePair(rows) {
      const oilRows = rows.filter(function (row) { return rowRole(row) === 'oil'; });
      const serviceRows = rows.filter(function (row) { return rowRole(row) === 'service'; });
      const oil = oilRows[0] || null;
      const service = serviceRows[0] || null;
      const ratio = pairRatio(rows);
      const linkedVariant = String(service && service.getAttribute('data-finished-perfume-oil-variant') || '').trim();
      const oilVariant = String(oil && oil.getAttribute('data-variant-id') || '').trim();
      const structureValid = rows.length === 2 && oilRows.length === 1 && serviceRows.length === 1 &&
        rows.every(function (row) { return !!row.getAttribute('data-line-key'); }) &&
        !!ratio && (!linkedVariant || !oilVariant || linkedVariant === oilVariant);
      const quantitiesValid = structureValid && rowQuantity(oil) > 0 &&
        rowQuantity(service) === rowQuantity(oil) * ratio;
      return {
        valid: structureValid && quantitiesValid,
        structureValid: structureValid,
        oil: oil,
        service: service,
        ratio: ratio
      };
    }

    function pairGroups() {
      const groups = new Map();
      form.querySelectorAll('[data-cart-line][data-finished-perfume-batch]').forEach(function (row) {
        const batch = String(row.getAttribute('data-finished-perfume-batch') || '').trim();
        if (!batch) return;
        if (!groups.has(batch)) groups.set(batch, []);
        groups.get(batch).push(row);
      });
      return groups;
    }

    function validatePairs(options) {
      const settings = options || {};
      let invalid = false;
      let incomplete = false;
      form.querySelectorAll('[data-cart-line]').forEach(function (row) {
        row.classList.remove('is-finished-pair-invalid');
        row.removeAttribute('aria-invalid');
      });

      form.querySelectorAll('[data-cart-line][data-finished-perfume-service-product="true"]').forEach(function (row) {
        if (String(row.getAttribute('data-finished-perfume-batch') || '').trim()) return;
        invalid = true;
        incomplete = true;
        row.classList.add('is-finished-pair-invalid');
        row.setAttribute('aria-invalid', 'true');
      });

      pairGroups().forEach(function (rows) {
        const inspection = inspectPagePair(rows);
        if (inspection.valid) return;
        invalid = true;
        incomplete = incomplete || !inspection.structureValid;
        rows.forEach(function (row) {
          row.classList.add('is-finished-pair-invalid');
          row.setAttribute('aria-invalid', 'true');
        });
      });

      form.classList.toggle('has-invalid-finished-pair', invalid);
      if (checkoutButton) {
        checkoutButton.disabled = invalid;
        checkoutButton.setAttribute('aria-disabled', String(invalid));
      }
      if (invalid && settings.announce !== false) {
        announce(incomplete
          ? 'A finished-perfume set is incomplete. Remove it and add perfume preparation again before checkout.'
          : 'A finished-perfume set is out of sync. Adjust either quantity to update both items before checkout.', true);
      } else if (!invalid && status && status.dataset.finishedPerfumePairMessage === 'true') {
        announce('', false);
      }
      return !invalid;
    }

    function syncPair(row, requestedOilQuantity) {
      const batch = String(row && row.getAttribute('data-finished-perfume-batch') || '').trim();
      if (!batch) return false;
      const rows = batchRows(batch);
      const inspection = inspectPagePair(rows);
      if (!inspection.structureValid) {
        validatePairs();
        return false;
      }
      const oilInput = inspection.oil.querySelector('[data-cart-qty]');
      const serviceInput = inspection.service.querySelector('[data-cart-qty]');
      if (!oilInput || !serviceInput) return false;
      const oilQuantity = Math.max(0, Math.round(Number(requestedOilQuantity || 0)));
      oilInput.value = String(oilQuantity);
      serviceInput.value = String(oilQuantity * inspection.ratio);
      validatePairs({ announce: false });
      return true;
    }

    function scheduleUpdate() {
      if (!autoUpdate || !updateButton) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        if (form.requestSubmit) form.requestSubmit(updateButton);
        else updateButton.click();
      }, 420);
    }

    scope.listen(form, 'click', function (event) {
      const remove = event.target.closest('[data-cart-line-remove]');
      if (remove) {
        const removeRow = remove.closest('[data-cart-line]');
        const batch = String(removeRow && removeRow.getAttribute('data-finished-perfume-batch') || '').trim();
        if (!batch) return;
        event.preventDefault();
        window.clearTimeout(timer);
        batchRows(batch).forEach(function (row) {
          const input = row.querySelector('[data-cart-qty]');
          if (input) input.value = '0';
        });
        announce('Removing both items in the finished-perfume set.', false);
        if (updateButton) {
          if (form.requestSubmit) form.requestSubmit(updateButton);
          else updateButton.click();
        }
        return;
      }

      const decrement = event.target.closest('[data-cart-line-minus]');
      const increment = event.target.closest('[data-cart-line-plus]');
      if (!decrement && !increment) return;
      event.preventDefault();
      const stepper = event.target.closest('.cart-qty-stepper');
      const input = stepper && stepper.querySelector('[data-cart-qty]');
      if (!input) return;
      const row = input.closest('[data-cart-line]');
      const batch = String(row && row.getAttribute('data-finished-perfume-batch') || '').trim();
      if (batch) {
        const rows = batchRows(batch);
        const inspection = inspectPagePair(rows);
        if (!inspection.structureValid) {
          validatePairs();
          return;
        }
        const currentOilQuantity = rowQuantity(inspection.oil);
        syncPair(inspection.oil, Math.max(0, currentOilQuantity + (increment ? 1 : -1)));
        scheduleUpdate();
        return;
      }
      const step = Number(input.step || 1) || 1;
      const min = input.min === '' ? 0 : Number(input.min);
      const max = input.max === '' ? Infinity : Number(input.max);
      const current = Number.isFinite(input.valueAsNumber) ? input.valueAsNumber : min;
      input.value = String(Math.min(max, Math.max(min, current + (increment ? step : -step))));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    scope.listen(form, 'change', function (event) {
      if (!event.target.matches('[data-cart-qty]')) return;
      const row = event.target.closest('[data-cart-line]');
      const batch = String(row && row.getAttribute('data-finished-perfume-batch') || '').trim();
      if (batch) {
        const rows = batchRows(batch);
        const inspection = inspectPagePair(rows);
        if (inspection.structureValid) {
          const requested = Math.max(0, Math.round(Number(event.target.value || 0)));
          const oilQuantity = rowRole(row) === 'service' && requested > 0
            ? Math.max(1, Math.round(requested / inspection.ratio))
            : requested;
          syncPair(row, oilQuantity);
        } else {
          validatePairs();
        }
      }
      scheduleUpdate();
    });

    scope.listen(form, 'submit', function (event) {
      const submitter = event.submitter || document.activeElement;
      if (!submitter || submitter.getAttribute('name') !== 'checkout') return;
      if (validatePairs()) return;
      event.preventDefault();
      if (status) status.focus({ preventScroll: false });
    });
    scope.listen(window, 'pageshow', function () { validatePairs(); });

    validatePairs();

    return {
      destroy: function () {
        window.clearTimeout(timer);
        scope.destroy();
      }
    };
  }

  function createCollectionSortController(select) {
    const scope = createScope();

    function syncFromURL() {
      try {
        const url = new URL(window.location.href);
        const requestedSort = url.searchParams.get('sort_by') || select.getAttribute('data-collection-default-sort') || '';
        const hasOption = Array.from(select.options).some(function (option) { return option.value === requestedSort; });
        if (hasOption) select.value = requestedSort;
      } catch (error) {}
    }

    syncFromURL();
    scope.listen(window, 'popstate', function () {
      window.setTimeout(syncFromURL, 0);
    });
    scope.listen(select, 'change', function () {
      if (select.form) {
        if (select.form.requestSubmit) select.form.requestSubmit();
        else select.form.submit();
        return;
      }
      try {
        const url = new URL(window.location.href);
        if (select.value) url.searchParams.set('sort_by', select.value);
        else url.searchParams.delete('sort_by');
        window.location.assign(url.pathname + url.search + url.hash);
      } catch (error) {}
    });
    return {
      pageShow: syncFromURL,
      destroy: scope.destroy
    };
  }

  function createSimilarOilsController(root) {
    const scope = createScope();
    const track = root.querySelector('[data-similar-oils-track]');
    const status = root.querySelector('[data-similar-oils-status]');
    const moreButton = root.querySelector('[data-similar-oils-more]');
    const currentProfileElement = root.querySelector('[data-current-scent-profile]');
    const indexUrl = root.getAttribute('data-index-url') || '';
    const pathMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
    const currentHandle = root.getAttribute('data-current-handle') || (pathMatch ? decodeURIComponent(pathMatch[1]) : '');
    const currentTitle = root.getAttribute('data-current-title') || '';
    const NOTE_WEIGHT = 0.80;
    const ACCORD_WEIGHT = 0.20;
    const MINIMUM_MATCHES = 2;
    const INITIAL_MATCHES = 4;
    const MAX_MATCHES = 8;
    const TERM_ALIASES = new Map([
      ['agarwood', 'oud'],
      ['agarwood oud', 'oud'],
      ['cedar wood', 'cedar'],
      ['cedarwood', 'cedar'],
      ['oak moss', 'oakmoss'],
      ['vanille', 'vanilla'],
      ['vetyver', 'vetiver']
    ]);
    const DISALLOWED_TERM_KEYS = new Set([
      ['raw', 'aroma', 'chem'].join(''),
      ['raw', 'aroma', 'chem'].join(' '),
      ['same', 'direction'].join(' '),
      ['brand', 'direction'].join(' '),
      'direction',
      'mood',
      ['sup', 'plier'].join(''),
      'vendor',
      'collection',
      ['product', 'type'].join(' ')
    ]);
    const scentPlaceholderPart = ['aroma', 'chem'].join('');
    const PLACEHOLDER_MARKERS = [
      ['raw', scentPlaceholderPart].join(''),
      ['raw', scentPlaceholderPart].join(' '),
      ['olfactive', 'atelier'].join(''),
      ['olfactive', 'atelier'].join(' '),
      ['direct', 'capture'].join(' '),
      ['direct', 'page', 'capture'].join(' '),
      ['load', 'ing'].join(''),
      ['cur', 'ated'].join('')
    ];
    let fetchController = null;
    let ready = false;
    let loading = false;
    let idleId = 0;
    const livePriceCache = window.PellairLiveProductPriceCache instanceof Map
      ? window.PellairLiveProductPriceCache
      : new Map();
    window.PellairLiveProductPriceCache = livePriceCache;
    const livePriceQueue = [];
    let activeLivePriceRequests = 0;

    function cleanDisplayText(value, maximumLength) {
      return String(value == null ? '' : value)
        .replace(/<[^>]*>/g, ' ')
        .replace(/[\u0000-\u001f\u007f]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, maximumLength || 120);
    }

    function canonicalTerms(values) {
      const terms = new Map();

      function add(value) {
        if (Array.isArray(value)) {
          value.forEach(add);
          return;
        }
        if (typeof value !== 'string' && typeof value !== 'number') return;
        String(value).split(/[,;\n|]+/).forEach(function (part) {
          const label = cleanDisplayText(part, 80).replace(/^(?:parfumo|wikiparfum)\s*:\s*/i, '').trim();
          const normalizedKey = normalize(label);
          if (PLACEHOLDER_MARKERS.some(function (marker) { return normalizedKey.indexOf(marker) !== -1; })) return;
          const key = TERM_ALIASES.get(normalizedKey) || normalizedKey;
          if (label && key && !DISALLOWED_TERM_KEYS.has(key) && !terms.has(key)) terms.set(key, { key: key, label: label });
        });
      }

      add(values);
      return Array.from(terms.values());
    }

    function credibleNoteTerms(noteValues, accordTerms) {
      return canonicalTerms(noteValues);
    }

    function handleFromProduct(product) {
      const direct = product && (product.handle || product.shopifyHandle);
      if (direct) return String(direct);
      const url = product && product.url || '';
      return String(url).split('/').filter(Boolean).pop() || '';
    }

    function productLink(product) {
      const handle = product && (product.handle || product.shopifyHandle);
      if (handle) {
        const rootRoute = String(routes.root || '/').replace(/\/?$/, '/');
        return rootRoute + 'products/' + encodeURIComponent(handle);
      }
      return safeHref(product && product.url, routes.allProducts || '/collections/all');
    }

    function prepareDocument(product) {
      const title = cleanDisplayText(product && product.title, 160);
      const titleKey = normalize(title);
      const blockedIdentity = titleKey.indexOf(['raw', 'aroma', 'chem'].join('')) !== -1 ||
        titleKey.indexOf(['raw', 'aroma', 'chem'].join(' ')) !== -1;
      const accords = canonicalTerms(product && product.accords);
      const notes = credibleNoteTerms(product && product.notes, accords);
      return {
        product: product,
        handleKey: normalize(handleFromProduct(product)),
        titleKey: titleKey,
        blockedIdentity: blockedIdentity,
        notes: notes,
        accords: accords
      };
    }

    function buildFieldStatistics(documents, field) {
      const frequencies = new Map();
      const total = Math.max(1, documents.length);
      documents.forEach(function (documentProfile) {
        const present = new Set(documentProfile[field].map(function (term) { return term.key; }));
        present.forEach(function (key) { frequencies.set(key, (frequencies.get(key) || 0) + 1); });
      });
      const statistics = new Map();
      frequencies.forEach(function (frequency, key) {
        statistics.set(key, {
          frequency: frequency,
          ratio: frequency / total,
          idf: Math.log((total + 1) / (frequency + 1)) + 1
        });
      });
      return statistics;
    }

    function makeFieldVector(terms, statistics) {
      const values = new Map();
      let normSquared = 0;
      terms.forEach(function (term) {
        const statistic = statistics.get(term.key);
        if (!statistic) return;
        values.set(term.key, {
          key: term.key,
          label: term.label,
          idf: statistic.idf,
          ratio: statistic.ratio
        });
        normSquared += statistic.idf * statistic.idf;
      });
      return { values: values, normSquared: normSquared };
    }

    function makeVector(profile, noteStatistics, accordStatistics) {
      return {
        notes: makeFieldVector(profile.notes, noteStatistics),
        accords: makeFieldVector(profile.accords, accordStatistics)
      };
    }

    function sharedTerms(left, right) {
      const output = [];
      left.values.forEach(function (term, key) {
        if (right.values.has(key)) output.push(term);
      });
      return output.sort(function (a, b) {
        return b.idf - a.idf || a.label.localeCompare(b.label, 'en', { sensitivity: 'base' });
      });
    }

    function mergeTerms(primary, secondary) {
      const merged = new Map();
      [primary || [], secondary || []].forEach(function (terms) {
        terms.forEach(function (term) {
          if (term && term.key && !merged.has(term.key)) merged.set(term.key, term);
        });
      });
      return Array.from(merged.values());
    }

    function fieldDot(left, right) {
      let dot = 0;
      left.values.forEach(function (term, key) {
        if (right.values.has(key)) dot += term.idf * term.idf;
      });
      return dot;
    }

    function score(currentVector, candidateVector, product) {
      const notes = sharedTerms(currentVector.notes, candidateVector.notes);
      const accords = sharedTerms(currentVector.accords, candidateVector.accords);
      const tier = notes.length >= 2 ? 1 : (notes.length >= 1 && accords.length >= 2 ? 2 : (accords.length >= 3 ? 3 : 0));
      if (!tier) return null;

      const noteInformation = notes.reduce(function (total, term) { return total + term.idf; }, 0);
      const accordInformation = accords.reduce(function (total, term) { return total + term.idf; }, 0);

      const dot = NOTE_WEIGHT * fieldDot(currentVector.notes, candidateVector.notes) +
        ACCORD_WEIGHT * fieldDot(currentVector.accords, candidateVector.accords);
      const currentNorm = NOTE_WEIGHT * currentVector.notes.normSquared +
        ACCORD_WEIGHT * currentVector.accords.normSquared;
      const candidateNorm = NOTE_WEIGHT * candidateVector.notes.normSquared +
        ACCORD_WEIGHT * candidateVector.accords.normSquared;
      const denominator = Math.sqrt(currentNorm * candidateNorm);
      const similarity = denominator > 0 ? dot / denominator : 0;
      if (!Number.isFinite(similarity) || similarity <= 0) return null;

      return {
        product: product,
        notes: notes,
        accords: accords,
        tier: tier,
        noteInformation: noteInformation,
        accordInformation: accordInformation,
        score: similarity,
        tieBreak: stablePairHash(product)
      };
    }

    function reason(match) {
      let label = 'Shared notes';
      let terms = match.notes;
      if (match.tier === 2) {
        label = 'Shared note and character';
        terms = match.notes.slice(0, 1).concat(match.accords.slice(0, 2));
      } else if (match.tier === 3) {
        label = 'Similar character';
        terms = match.accords;
      }
      const visible = terms.slice(0, 2).map(function (term) { return term.label; });
      const remaining = Math.max(0, terms.length - visible.length);
      return {
        label: label,
        text: visible.join(' \u00b7 ') + (remaining ? ' +' + remaining + ' more' : '')
      };
    }

    function stablePairHash(product) {
      const value = normalize(currentHandle) + '|' + normalize(handleFromProduct(product));
      let hash = 2166136261;
      for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
      }
      return hash >>> 0;
    }

    function compareText(left, right) {
      const normalizedLeft = normalize(left);
      const normalizedRight = normalize(right);
      if (normalizedLeft < normalizedRight) return -1;
      if (normalizedLeft > normalizedRight) return 1;
      const rawLeft = String(left || '');
      const rawRight = String(right || '');
      return rawLeft < rawRight ? -1 : (rawLeft > rawRight ? 1 : 0);
    }

    function compareMatches(left, right) {
      if (left.tier !== right.tier) return left.tier - right.tier;
      if (left.notes.length !== right.notes.length) return right.notes.length - left.notes.length;
      if (left.noteInformation !== right.noteInformation) return right.noteInformation - left.noteInformation;
      if (left.score !== right.score) return right.score - left.score;
      if (left.accords.length !== right.accords.length) return right.accords.length - left.accords.length;
      if (left.accordInformation !== right.accordInformation) return right.accordInformation - left.accordInformation;
      if (left.tieBreak !== right.tieBreak) return left.tieBreak - right.tieBreak;
      return compareText(handleFromProduct(left.product), handleFromProduct(right.product));
    }

    function readCurrentProfile() {
      if (!currentProfileElement) return { notes: [], accords: [] };
      try {
        const profile = JSON.parse(currentProfileElement.textContent.trim() || '{}');
        const profileAccords = canonicalTerms(profile && profile.accords);
        return {
          notes: credibleNoteTerms(profile && profile.notes, profileAccords),
          accords: profileAccords
        };
      } catch (error) {
        return { notes: [], accords: [] };
      }
    }

    function imageWithWidth(value, width) {
      const image = safeImage(value);
      if (!image) return '';
      try {
        const parsed = new URL(image, window.location.href);
        parsed.searchParams.set('width', String(width));
        return parsed.href;
      } catch (error) {
        return image;
      }
    }

    function titleParts(value) {
      const title = cleanDisplayText(value, 160) || 'Pellair oil';
      const match = title.match(/^(.+?)\s+(?:-|\u2013|\u2014)\s+(.+)$/);
      return match
        ? { full: title, house: match[1].trim(), scent: match[2].trim() }
        : { full: title, house: '', scent: title };
    }

    function runLivePriceQueue() {
      while (activeLivePriceRequests < 4 && livePriceQueue.length) {
        const task = livePriceQueue.shift();
        activeLivePriceRequests += 1;
        task().finally(function () {
          activeLivePriceRequests -= 1;
          runLivePriceQueue();
        });
      }
    }

    function liveProductPrice(handle) {
      const cleanHandle = cleanDisplayText(handle, 160);
      if (!cleanHandle) return Promise.reject(new Error('Missing product handle'));
      if (livePriceCache.has(cleanHandle)) return livePriceCache.get(cleanHandle);
      const request = new Promise(function (resolve, reject) {
        livePriceQueue.push(function () {
          const rootRoute = String(routes.root || '/').replace(/\/?$/, '/');
          return fetch(rootRoute + 'products/' + encodeURIComponent(cleanHandle) + '.js', {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' }
          }).then(function (response) {
            if (!response.ok) throw new Error('Product price unavailable');
            return response.json();
          }).then(resolve, reject);
        });
        runLivePriceQueue();
      });
      livePriceCache.set(cleanHandle, request);
      request.catch(function () {
        if (livePriceCache.get(cleanHandle) === request) livePriceCache.delete(cleanHandle);
      });
      return request;
    }

    function livePriceMarkup(product) {
      const minimum = Number(product && (product.price_min != null ? product.price_min : product.price));
      const maximum = Number(product && (product.price_max != null ? product.price_max : minimum));
      if (!Number.isFinite(minimum) || minimum < 0) return '';
      const varies = Boolean(product.price_varies || maximum > minimum);
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const saleVariant = variants.find(function (variant) {
        return Number(variant.price) === minimum && Number(variant.compare_at_price || 0) > Number(variant.price || 0);
      });
      const compare = saleVariant ? Number(saleVariant.compare_at_price || 0) : 0;
      const rangeLabel = varies && maximum > minimum
        ? 'Prices range from ' + formatMoney(minimum, theme.currency) + ' to ' + formatMoney(maximum, theme.currency) + '.'
        : 'Price ' + formatMoney(minimum, theme.currency) + '.';
      return '<span class="visually-hidden">' + escapeHTML(rangeLabel) + '</span>' +
        (varies ? '<span class="price__from" aria-hidden="true">From</span>' : '') +
        (compare > minimum ? '<s class="price__compare" aria-hidden="true">' + escapeHTML(formatMoney(compare, theme.currency)) + '</s>' : '') +
        '<strong class="price__current" aria-hidden="true">' + escapeHTML(formatMoney(minimum, theme.currency)) + '</strong>';
    }

    function hydrateLivePrices(container) {
      if (!container) return;
      container.querySelectorAll('[data-live-product-price]').forEach(function (slot) {
        if (slot.closest('[hidden]') || slot.getAttribute('data-price-requested') === 'true') return;
        slot.setAttribute('data-price-requested', 'true');
        liveProductPrice(slot.getAttribute('data-product-handle') || '').then(function (product) {
          if (!slot.isConnected) return;
          const markup = livePriceMarkup(product);
          slot.setAttribute('aria-busy', 'false');
          if (!markup) {
            slot.hidden = true;
            return;
          }
          slot.innerHTML = markup;
          slot.classList.add('is-loaded');
        }).catch(function () {
          if (!slot.isConnected) return;
          slot.setAttribute('aria-busy', 'false');
          slot.hidden = true;
        });
      });
    }

    function card(match, index) {
      const product = match.product;
      const href = productLink(product);
      const image = safeImage(product.image);
      const titles = titleParts(product.title);
      const imageAlt = cleanDisplayText(product.imageAlt, 160) || titles.full;
      const handle = handleFromProduct(product);
      const relation = reason(match);
      const source = imageWithWidth(image, 480);
      const sourceSet = [320, 480, 720].map(function (width) {
        return imageWithWidth(image, width) + ' ' + width + 'w';
      }).join(', ');
      const loading = index < INITIAL_MATCHES ? 'eager' : 'lazy';
      const imageMarkup = image
        ? '<img src="' + escapeHTML(source) + '" srcset="' + escapeHTML(sourceSet) + '" sizes="(max-width: 749px) calc(50vw - 1.5rem), 25vw" alt="' + escapeHTML(imageAlt) + '" width="720" height="960" loading="' + loading + '" decoding="async">'
        : '<span class="similar-oil-card__placeholder" aria-hidden="true">Pellair Oils</span>';
      const extraAttributes = index >= INITIAL_MATCHES ? ' data-similar-oils-extra hidden' : '';
      return '<li class="similar-oil-card"' + extraAttributes + '>' +
        '<a class="similar-oil-card__link" href="' + escapeHTML(href) + '">' +
          '<div class="similar-oil-card__media">' + imageMarkup + '</div>' +
          '<div class="similar-oil-card__body">' +
            (titles.house ? '<span class="similar-oil-card__house">' + escapeHTML(titles.house) + '</span>' : '') +
            '<h3>' + escapeHTML(titles.scent) + '</h3>' +
            '<span class="similar-oil-card__reason"><span class="visually-hidden">' + escapeHTML(relation.label) + ': </span>' + escapeHTML(relation.text) + '</span>' +
            '<span class="similar-oil-card__price price" data-live-product-price data-product-handle="' + escapeHTML(handle) + '" aria-busy="true">' +
              '<span class="similar-oil-card__price-skeleton" aria-hidden="true"></span>' +
            '</span>' +
          '</div>' +
        '</a>' +
      '</li>';
    }

    function setStatus(message, error) {
      if (status) {
        status.textContent = message || '';
        status.hidden = !message;
        status.classList.toggle('is-error', !!error);
      }
      root.classList.toggle('has-error', !!error);
    }

    function hideSection() {
      if (track) track.replaceChildren();
      if (moreButton) {
        moreButton.hidden = true;
        moreButton.setAttribute('aria-expanded', 'false');
      }
      root.classList.remove('is-ready', 'has-error');
      root.hidden = true;
      setStatus('');
    }

    if (moreButton) {
      scope.listen(moreButton, 'click', function () {
        track.querySelectorAll('[data-similar-oils-extra]').forEach(function (cardElement) {
          cardElement.hidden = false;
          cardElement.removeAttribute('data-similar-oils-extra');
        });
        moreButton.hidden = true;
        moreButton.setAttribute('aria-expanded', 'true');
        hydrateLivePrices(track);
      });
    }

    async function load() {
      if (loading || ready || !indexUrl || !track || !currentHandle) return;
      loading = true;
      root.setAttribute('aria-busy', 'true');
      if (fetchController) fetchController.abort();
      const controller = new AbortController();
      fetchController = controller;
      try {
        const index = await fetchJSON(indexUrl, {
          credentials: 'same-origin',
          cache: 'force-cache',
          headers: { Accept: 'application/json' },
          signal: controller.signal
        });
        const products = Array.isArray(index.products) ? index.products.filter(function (product) {
          return product && typeof product === 'object';
        }) : [];
        const documents = products.map(prepareDocument);
        const noteStatistics = buildFieldStatistics(documents, 'notes');
        const accordStatistics = buildFieldStatistics(documents, 'accords');
        const currentKey = normalize(currentHandle);
        const handleCurrent = documents.find(function (documentProfile) {
          return documentProfile.handleKey === currentKey;
        });
        const currentTitleKey = normalize(currentTitle);
        const titleCandidates = currentTitleKey ? documents.filter(function (documentProfile) {
          return documentProfile.titleKey === currentTitleKey;
        }) : [];
        const indexedCurrent = handleCurrent || (titleCandidates.length === 1 ? titleCandidates[0] : null);
        const excludedHandle = indexedCurrent ? indexedCurrent.handleKey : currentKey;
        const liveCurrent = readCurrentProfile();
        const current = {
          notes: mergeTerms(liveCurrent.notes, indexedCurrent ? indexedCurrent.notes : []),
          accords: mergeTerms(liveCurrent.accords, indexedCurrent ? indexedCurrent.accords : [])
        };
        if (!current.notes.length && !current.accords.length) {
          ready = true;
          hideSection();
          return;
        }
        const currentVector = makeVector(current, noteStatistics, accordStatistics);
        const seenHandles = new Set();
        const seenImages = new Set();
        const excludedTitle = indexedCurrent ? indexedCurrent.titleKey : currentTitleKey;
        const matches = documents.map(function (documentProfile) {
          if (!documentProfile.handleKey || documentProfile.handleKey === excludedHandle || documentProfile.blockedIdentity) return null;
          if (excludedTitle && documentProfile.titleKey === excludedTitle) return null;
          const candidateVector = makeVector(documentProfile, noteStatistics, accordStatistics);
          return score(currentVector, candidateVector, documentProfile.product);
        }).filter(Boolean).sort(compareMatches).filter(function (match) {
          const handleKey = normalize(handleFromProduct(match.product));
          const imageKey = normalize(safeImage(match.product && match.product.image).replace(/[?#].*$/, ''));
          if (handleKey && seenHandles.has(handleKey)) return false;
          if (imageKey && seenImages.has(imageKey)) return false;
          if (handleKey) seenHandles.add(handleKey);
          if (imageKey) seenImages.add(imageKey);
          return true;
        }).slice(0, MAX_MATCHES);
        if (matches.length < MINIMUM_MATCHES) {
          ready = true;
          hideSection();
          return;
        }
        track.innerHTML = matches.map(card).join('');
        hydrateLivePrices(track);
        if (moreButton) {
          const remaining = Math.max(0, matches.length - INITIAL_MATCHES);
          moreButton.hidden = remaining === 0;
          moreButton.setAttribute('aria-expanded', 'false');
          moreButton.textContent = remaining ? 'Show ' + remaining + ' more' : '';
          moreButton.setAttribute('aria-label', remaining ? 'Show ' + remaining + ' more related oils' : '');
        }
        root.classList.add('is-ready');
        root.hidden = false;
        ready = true;
        setStatus('');
        reveals.observeWithin(root);
      } catch (error) {
        if (isAbortError(error)) return;
        ready = true;
        hideSection();
      } finally {
        if (fetchController === controller) {
          fetchController = null;
          loading = false;
        }
        root.setAttribute('aria-busy', 'false');
      }
    }

    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(load, { timeout: 1400 });
      scope.add(function () { window.cancelIdleCallback(idleId); });
    } else {
      scope.timeout(load, 500);
    }

    return {
      pageHide: function () { if (fetchController) fetchController.abort(); },
      pageShow: function () {
        if (!ready && fetchController && fetchController.signal.aborted) {
          fetchController = null;
          loading = false;
        }
        if (!ready) load();
      },
      destroy: function () {
        if (fetchController) fetchController.abort();
        scope.destroy();
      }
    };
  }

  function createContactPrefillController(section) {
    const form = section.querySelector('[data-contact-form]');
    if (!form || !window.URLSearchParams) return { destroy: function () {} };
    const subjectInput = form.querySelector('input[name="contact[subject]"]');
    const messageInput = form.querySelector('textarea[name="contact[body]"]');
    const params = new URLSearchParams(window.location.search);
    const clean = function (name, limit) {
      return String(params.get(name) || '')
        .replace(/[\u0000-\u001f\u007f]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, limit || 160);
    };
    const subject = clean('subject', 120);
    const product = clean('product', 160);
    const variant = clean('variant', 120);
    const concentration = clean('concentration', 80);
    const estimatedYield = clean('yield', 80);
    if (!subject && !product && !variant && !concentration && !estimatedYield) {
      return { destroy: function () {} };
    }

    if (subjectInput && !subjectInput.value.trim()) {
      subjectInput.value = subject || 'Custom glass bottle catalogue';
    }
    if (messageInput && !messageInput.value.trim()) {
      const lines = ['I would like the glass bottle catalogue, customization choices and a packaging quote.'];
      if (product) lines.push('', 'Product: ' + product);
      if (variant) lines.push('Oil option: ' + variant);
      if (concentration) lines.push('Preferred concentration: ' + concentration);
      if (estimatedYield) lines.push('Estimated yield: ' + estimatedYield);
      messageInput.value = lines.join('\n');
    }
    section.classList.add('is-glass-enquiry');
    return { destroy: function () { section.classList.remove('is-glass-enquiry'); } };
  }

  function initWithin(root) {
    queryAll(root, '[data-server-form-response]').forEach(function (element) {
      if (element.getAttribute('data-response-focused') === 'true') return;
      element.setAttribute('data-response-focused', 'true');
      window.requestAnimationFrame(function () {
        try {
          element.focus({ preventScroll: true });
        } catch (error) {
          element.focus();
        }
      });
    });
    queryAll(root, '[data-header]').forEach(function (element) {
      mount('header', element, createHeaderController);
    });
    queryAll(root, '[data-cart-drawer]').forEach(function (element) {
      mount('cart-drawer', element, createCartDrawerController);
    });
    queryAll(root, '[data-brand-assembly]').forEach(function (element) {
      mount('assembly', element, createAssemblyController);
    });
    queryAll(root, '[data-product-form]').forEach(function (element) {
      mount('product-form', element, createProductFormController);
    });
    queryAll(root, '[data-mobile-sticky-purchase]').forEach(function (element) {
      mount('mobile-sticky-purchase', element, createMobileStickyPurchaseController);
    });
    queryAll(root, '[data-cart-page-form]').forEach(function (element) {
      mount('cart-page', element, createCartPageController);
    });
    queryAll(root, '[data-collection-sort]').forEach(function (element) {
      mount('collection-sort', element, createCollectionSortController);
    });
    queryAll(root, '[data-similar-oils], [data-pellair-similar-oils]').forEach(function (element) {
      mount('similar-oils', element, createSimilarOilsController);
    });
    queryAll(root, '[data-contact-section]').forEach(function (element) {
      mount('contact-prefill', element, createContactPrefillController);
    });
    reveals.observeWithin(root);
  }

  function settleTransientState() {
    html.classList.remove('cart-drawer-is-open', 'mobile-nav-open');
    document.body.classList.remove('cart-drawer-is-open', 'mobile-nav-open');
  }

  function destroyAll() {
    Array.from(controllerRecords).forEach(destroyRecord);
    reveals.destroy();
    lifecycleScope.destroy();
    settleTransientState();
    html.classList.remove('js');
    theme.cart = null;
  }

  lifecycleScope.listen(document, 'shopify:section:load', function (event) {
    initWithin(event.target);
  });
  lifecycleScope.listen(document, 'shopify:section:unload', function (event) {
    reveals.removeWithin(event.target);
    destroyWithin(event.target);
  });
  lifecycleScope.listen(document, 'shopify:section:select', function (event) {
    reveals.revealWithin(event.target);
    Array.from(controllerRecords).forEach(function (record) {
      if (containsOrIs(event.target, record.element) && record.controller && typeof record.controller.editorSelect === 'function') {
        record.controller.editorSelect(event);
      }
    });
  });
  lifecycleScope.listen(document, 'shopify:block:select', function (event) {
    reveals.revealWithin(event.target);
  });
  lifecycleScope.listen(window, 'pagehide', function (event) {
    invokeLifecycle('pageHide', event);
    settleTransientState();
  });
  lifecycleScope.listen(window, 'pageshow', function (event) {
    if (!event.persisted) return;
    settleTransientState();
    initWithin(document);
    invokeLifecycle('pageShow', event);
  });

  theme.__runtimeVersion = '28.11.0';
  theme.init = initWithin;
  theme.destroy = destroyWithin;
  theme.destroyAll = destroyAll;
  theme.formatMoney = formatMoney;
  window.PellairTheme = theme;

  if (document.readyState === 'loading') {
    lifecycleScope.listen(document, 'DOMContentLoaded', function () { initWithin(document); }, { once: true });
  } else {
    initWithin(document);
  }

  try {
    document.dispatchEvent(new CustomEvent('pellair:theme-ready', { detail: { theme: theme } }));
  } catch (error) {}
})();
